import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn
} from '@repo/ui'
import {
  AlertTriangle,
  ArrowRight,
  Cable,
  Check,
  CheckCircle2,
  ChevronLeft,
  CircleDashed,
  Copy,
  Database,
  Download,
  FileText,
  Library,
  Loader2,
  Mail,
  Maximize2,
  MoreHorizontal,
  PanelRight,
  Pencil,
  Plus,
  Presentation as PresentationIcon,
  X
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import { PaneResizer } from '@/components/PaneResizer'
import { presentationFitIssueLabels, presentationFitLabel } from '@/components/presentation/presentationFit'
import { InlineEditableText } from '@/components/proposal/InlineEditableText'
import { ProposalSourcesPanel } from '@/components/proposal/ProposalSourcesPanel'
import type {
  ConversationBrief,
  ConversationBriefCorrection,
  ConversationExecution,
  ConversationExecutionRequest,
  ConversationRedraftRequest,
  ConversationRun,
  ConversationStaleness
} from '@/api/conversation'
import { deleteDocument, exportDocument, getDocument, renameDocument } from '@/api/documents/documents'
import type { DocumentRevision, WorkspaceDocument } from '@/api/documents/documents'
import {
  PresentationLayout,
  cancelPresentationBuildRun,
  deselectPresentationSlideVersion,
  exportPresentation,
  getLatestPresentationBuildRun,
  getPresentation,
  getPresentationBuildRun,
  getPresentationMessages,
  getPresentationSlideMessages,
  listPresentations,
  refinePresentationDeck,
  refinePresentationSlide,
  renamePresentation,
  retryPresentationBuildRun,
  selectPresentationSlideVersion,
  startPresentationBuildRun,
  updatePresentationSlide
} from '@/api/presentations/presentations'
import type {
  Presentation,
  PresentationMessage,
  PresentationSlide,
  PresentationSourceArtifact,
  UpdatePresentationSlideRequest
} from '@/api/presentations/presentations'
import {
  cancelProposalBuildRun,
  createSection,
  deleteProposal,
  deleteSection,
  deselectSectionVersion,
  editSection,
  exportProposal,
  getLatestProposalBuildRun,
  getProposal,
  getProposalBuildRun,
  getSectionMessages,
  listProposals,
  renameProposal,
  renameSection,
  reorderSections,
  retryProposalBuildRun,
  selectSectionVersion,
  startProposalBuildRun
} from '@/api/proposals/proposals'
import { RagFileUploadState, fetchRagFiles } from '@/api/content/file'
import { fetchRagTexts } from '@/api/content/text'
import { fetchSourceSummaries } from '@/api/content/refs'
import { listConnectors } from '@/api/content/connector'
import type { AgentRun, Proposal, ProposalSection } from '@/api/proposals/proposals'
import { failureLine } from '@/common/errorMessages'
import { proposalBuildNeedsFreshRun } from '@/common/proposalBuildRecovery'
import { useSourceDocumentLabel } from '@/common/useSourceDocumentLabel'
import { ComputedResultFallback, ComputedResultTable } from './ComputedResult'
import { SourceSummaryRow, useSourceDetails } from './SourceDetails'
import { safeCitationIDs } from './chatContextState'
import { selectConversationTurnPresentations, selectFactSpine } from './state'
import { changeSizeLabel, stageLabel } from './eventCopy'
import { loadConversationDocuments } from './options'
import { useCanvasOutline } from './canvasOutline'
import { useArtifactRevision } from './artifactChanges'
import {
  PresentationSlideAcceptance,
  PresentationSlideVersions,
  ProposalSectionAcceptance,
  ProposalSectionVersions
} from './canvasVersions'
import { usePanelArtifact } from './canvas/usePanelArtifact'
import { PanelFailure, PanelHeader } from './canvas/PanelChrome'
import {
  ConnectorPanel,
  FilePanel,
  TextPanel,
  loadContentConnector,
  loadContentFile,
  loadContentText
} from './canvas/ContentPanels'
import { BuildRunStrip } from './canvas/BuildRunStrip'
import { CanvasConfirmDialog } from './canvas/CanvasConfirmDialog'
import { AddSectionRow, ProposalSectionActions } from './canvas/ProposalStructuralControls'
import { CanvasEditActions, CanvasEditSaveAnnouncement, useCanvasEditShortcuts } from './canvas/CanvasEditActions'
import { InlineArtifactInput, InlineArtifactTextarea } from './canvas/InlineArtifactField'
import { ProposalCanvasBodyEditor } from './canvas/ProposalCanvasBodyEditor'
import { RefineComposer } from './canvas/RefineComposer'
import { usePresentationSlideDraft } from './canvas/usePresentationSlideDraft'
import { useSectionRefine } from './canvas/useSectionRefine'
import { PresentationBuildProgress, ProposalBuildProgress, isActiveBuildRun } from './canvas/BuildRunProgress'
import type { BuildRunStripControls } from './canvas/BuildRunStrip'
import type { CanvasProvenance } from './canvas/PanelChrome'
import type {
  ContentConnector,
  ContentFile,
  ContentPanelServices,
  ContentPanelTarget,
  ContentText
} from './canvas/ContentPanels'
import type { MutableRefObject, ReactNode, RefObject } from 'react'
import type { ConversationComposerLoadRequest, ConversationComposerOption, ConversationComposerPage } from './Composer'
import type { ConversationState, FactSpineEntry } from './state'
import type { ChatInspectorIdentity, ConversationCollectionKind, WorkspaceCanvasIdentity } from './ConversationShell'

type ConversationCollectionRow = {
  id: number
  title: string
  description: string
  status: string
  updatedAtTsMs: number
  sectionId?: number
}

// Which corpus the row came out of. Only the two content kinds carry one, because the
// Content collection is the one list that can span two: the workspace's own rows, and the
// organisation's, which every workspace reads and none owns. What a row's Open addresses,
// and what its Delete is sent with, both follow from this rather than from the workspace
// the collection happens to be listed for.
//
// It is required on those two rather than optional on all five, because the row a missing
// scope defaults to is the workspace's: a producer that emitted an organisation row without
// one would have it opened, and deleted, under this workspace's id, which addresses a copy
// the organisation row does not have. The other three kinds belong to the workspace by
// construction and say so by carrying no scope at all.
export type ConversationCollectionContentItem = ConversationCollectionRow & {
  kind: 'content' | 'text'
  scope: 'workspace' | 'global'
}

export type ConversationCollectionItem =
  | (ConversationCollectionRow & { kind: 'proposal' | 'presentation' | 'connector'; scope?: undefined })
  | ConversationCollectionContentItem

// Server-emitted fit-issue keys are open-ended: a key this build's label map does not know
// still renders as humanized words instead of nothing.
function fitIssueLabel(issue: string): string {
  return (presentationFitIssueLabels as Record<string, string | undefined>)[issue] ?? issue.replaceAll('_', ' ')
}

export type ConversationCollection = { items: Array<ConversationCollectionItem> }

// `partial` marks a page that is missing one of the sources it is drawn from. The panel
// says so rather than passing a short list off as the whole answer.
export type ConversationCollectionPage = ConversationCollection & { hasMore: boolean; partial?: true }
// globalContent is the organisation's switch rather than anything the reader typed: with it
// on, the Content collection is asked for the organisation's rows as well as the workspace's.
// It is left off entirely when the switch is off, so the request a workspace-only panel sends
// is the one it always sent.
export type ConversationCollectionRequest = { page: number; search: string; globalContent?: boolean }

const collectionPageSize = 20
const collectionTextTitleLength = 80

// A stored text is a body and nothing else: no name, no description, no state. Its row is
// titled by the first line of it that says anything, cut to what one line can hold. It has
// to be the first line with content rather than the first line: nothing trims a text on the
// way in, so one can open with blank lines, and titling such a row by an empty string leaves
// a row a reader cannot name and whose Open control announces as `Open text ` alongside
// every other one like it. A text holding nothing at all is named for what it is instead.
function textExcerpt(text: string): string {
  const line = text.split('\n').find((candidate) => candidate.trim() !== '')
  return line === undefined ? 'Empty text' : line.trim().slice(0, collectionTextTitleLength)
}

export async function loadConversationCollection(
  projectId: number,
  collection: ConversationCollectionKind,
  request: ConversationCollectionRequest = { page: 1, search: '' }
): Promise<ConversationCollectionPage> {
  if (!Number.isSafeInteger(projectId) || projectId <= 0) throw new Error('Workspace identity changed.')
  if (!Number.isSafeInteger(request.page) || request.page <= 0 || request.search.length > 200) {
    throw new Error('Collection request is invalid.')
  }
  const normalizedSearch = request.search.trim().toLocaleLowerCase()
  const paginate = (items: Array<ConversationCollectionItem>): ConversationCollectionPage => {
    const filtered = items
      .filter(
        (item) =>
          normalizedSearch === '' ||
          `${item.title} ${item.description} ${item.status}`.toLocaleLowerCase().includes(normalizedSearch)
      )
      .sort((left, right) => right.updatedAtTsMs - left.updatedAtTsMs || right.id - left.id)
    const offset = (request.page - 1) * collectionPageSize
    return {
      items: filtered.slice(offset, offset + collectionPageSize),
      hasMore: filtered.length > offset + collectionPageSize
    }
  }
  if (collection === 'proposals') {
    const response = await listProposals(projectId)
    return paginate(
      response.proposals
        .filter((proposal) => proposal.projectId === projectId)
        .map((proposal) => {
          // The list answers with the sections in whatever order it holds them, and two can
          // share an ordinal while a reorder is half applied, so the row opens at the same
          // section the rail would open: lowest ordinal, then lowest sectionId.
          const first = [...proposal.sections].sort(
            (left, right) => left.ordinal - right.ordinal || left.sectionId - right.sectionId
          )[0]
          return {
            kind: 'proposal' as const,
            id: proposal.proposalId,
            title: proposal.documentName,
            description: proposal.clientName,
            status: proposal.status,
            updatedAtTsMs: proposal.updatedAtTsMs,
            ...(first === undefined ? {} : { sectionId: first.sectionId })
          }
        })
    )
  }
  if (collection === 'presentations') {
    const presentations = await listPresentations(projectId)
    return paginate(
      presentations
        .filter((presentation) => presentation.projectId === projectId)
        .map((presentation) => ({
          kind: 'presentation' as const,
          id: presentation.presentationId,
          title: presentation.title,
          description: presentation.purpose,
          status: presentation.status,
          updatedAtTsMs: presentation.updatedAtTsMs
        }))
    )
  }
  if (collection === 'content') {
    // The corpus is uploaded files and stored texts, and both searches page server-side on
    // the same page number, so one request each covers the page the reader asked for. With
    // organisation content switched on there are four: each search answers with the
    // workspace's rows when it is given a workspace and the organisation's when it is given
    // none, and the two sets are disjoint, so no row can arrive twice.
    // Settled rather than all: these are separate reads of the same corpus, and one of them
    // failing should cost its own rows rather than blanking a collection the others could
    // still fill. Only every read failing is a collection that could not be loaded.
    const scopes: Array<'workspace' | 'global'> =
      request.globalContent === true ? ['workspace', 'global'] : ['workspace']
    const reads = await Promise.allSettled(
      scopes.flatMap((scope) => {
        // The organisation's corpus is what a search answers with when it is asked for no
        // workspace at all, so a global read leaves the filter off rather than sending a
        // value for it. ContentPanels reads a single organisation row the same way.
        const filter = scope === 'global' ? {} : { projectId }
        return [
          fetchRagFiles({ ...filter, currentPage: request.page, searchText: request.search }).then((response) => ({
            pageCount: response.pageCount,
            items: response.items.map((file): ConversationCollectionItem => ({
              kind: 'content',
              id: file.fileId,
              title: file.fileName,
              description: file.description,
              status: file.fileUploadState,
              updatedAtTsMs: file.updatedAtTsMs,
              scope
            }))
          })),
          fetchRagTexts({ ...filter, currentPage: request.page, searchText: request.search }).then((response) => ({
            pageCount: response.pageCount,
            items: response.items.map((text): ConversationCollectionItem => ({
              kind: 'text',
              id: text.docId,
              title: textExcerpt(text.text),
              description: '',
              status: '',
              updatedAtTsMs: text.updatedAtTsMs,
              scope
            }))
          }))
        ]
      })
    )
    const answered = reads.flatMap((read) => (read.status === 'fulfilled' ? [read.value] : []))
    if (answered.length === 0) throw new Error('The content collection could not be loaded.')
    const items = answered.flatMap((read) => read.items)
    // A page missing a source offers no way forward. One page number covers them all, so the
    // next one would ask the source that failed for a page past the one it never answered,
    // and those rows would be gone from the list for good. The reader's way back to them is
    // the search form, which returns to page one and asks every source again.
    if (answered.length < reads.length) return { items, hasMore: false, partial: true }
    return { items, hasMore: answered.some((read) => request.page < read.pageCount) }
  }
  const response = await listConnectors(projectId)
  return paginate(
    response.connectors
      .filter((connector) => connector.projectId === projectId)
      .map((connector) => ({
        kind: 'connector' as const,
        id: connector.id,
        title: connector.connectorDescription || connector.connectorType,
        description: connector.connectorType.replaceAll('-', ' '),
        status: connector.state,
        updatedAtTsMs: connector.updatedAtTsMs
      }))
  )
}

// Both hosts of the canvas bind loadCollection the same way: the collection they answer
// with is this workspace's, and one asked for on behalf of another is refused rather than
// answered with this one's. Written here rather than twice at the call sites because a
// binding that names the caller's arguments one at a time can leave the last one out and
// still type-check, which is what both of them did: the page and the search never reached
// the loader, so every collection sat on page one of an unfiltered list.
export function workspaceCollectionLoader(projectId: number): NonNullable<ConversationPanelServices['loadCollection']> {
  return (requestedProjectId, collection, request) => {
    if (requestedProjectId !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
    return loadConversationCollection(projectId, collection, request)
  }
}

// None of the chat-scoped services names a conversation: whoever renders the panel with
// a chat open has already closed over it. They used to be typed against the chat-less
// API functions, which had that exact shape by accident of being chat-less; those are
// retired, so the shape is now stated rather than borrowed.
export type ConversationPanelServices = {
  getBrief: (projectId: number, briefId: number) => Promise<ConversationBrief>
  correctBrief: (
    projectId: number,
    briefId: number,
    correction: ConversationBriefCorrection
  ) => Promise<ConversationBrief>
  executeBrief: (
    projectId: number,
    briefId: number,
    request: ConversationExecutionRequest
  ) => Promise<ConversationExecution>
  getOutputStaleness: (projectId: number, messageId: number) => Promise<ConversationStaleness>
  cancelExecution: (projectId: number, executionId: number) => Promise<ConversationRun>
  retryExecution: (projectId: number, executionId: number) => Promise<ConversationRun>
  redraftOutput: (
    projectId: number,
    messageId: number,
    request: ConversationRedraftRequest
  ) => Promise<ConversationExecution>
  getProposal: typeof getProposal
  getPresentation: typeof getPresentation
  getDocument: typeof getDocument
  exportProposal: typeof exportProposal
  exportPresentation: typeof exportPresentation
  exportDocument: typeof exportDocument
  getSectionMessages: typeof getSectionMessages
  selectSectionVersion: typeof selectSectionVersion
  deselectSectionVersion: typeof deselectSectionVersion
  getPresentationSlideMessages: typeof getPresentationSlideMessages
  selectPresentationSlideVersion: typeof selectPresentationSlideVersion
  deselectPresentationSlideVersion: typeof deselectPresentationSlideVersion
  // The mutation surface, artifact-scoped exactly like the reads and exports above. The
  // members are optional because not every host of the panel can offer them yet; the
  // defaults bind them to the real API functions so a host opts out by omission rather
  // than meeting a refusal that only exists to say no.
  createSection?: typeof createSection
  renameSection?: typeof renameSection
  deleteSection?: typeof deleteSection
  reorderSections?: typeof reorderSections
  deleteProposal?: typeof deleteProposal
  renameProposal?: typeof renameProposal
  editSection?: typeof editSection
  startProposalBuildRun?: typeof startProposalBuildRun
  retryProposalBuildRun?: typeof retryProposalBuildRun
  cancelProposalBuildRun?: typeof cancelProposalBuildRun
  getLatestProposalBuildRun?: typeof getLatestProposalBuildRun
  getProposalBuildRun?: typeof getProposalBuildRun
  updatePresentationSlide?: typeof updatePresentationSlide
  refinePresentationSlide?: typeof refinePresentationSlide
  refinePresentationDeck?: typeof refinePresentationDeck
  renamePresentation?: typeof renamePresentation
  startPresentationBuildRun?: typeof startPresentationBuildRun
  retryPresentationBuildRun?: typeof retryPresentationBuildRun
  cancelPresentationBuildRun?: typeof cancelPresentationBuildRun
  getLatestPresentationBuildRun?: typeof getLatestPresentationBuildRun
  getPresentationBuildRun?: typeof getPresentationBuildRun
  getPresentationMessages?: typeof getPresentationMessages
  deleteDocument?: typeof deleteDocument
  renameDocument?: typeof renameDocument
  getSourceSummaries: typeof fetchSourceSummaries
  loadFactDocuments: (
    projectId: number,
    request?: ConversationComposerLoadRequest
  ) => Promise<Array<ConversationComposerOption> | ConversationComposerPage>
  loadCollection?: (
    projectId: number,
    collection: ConversationCollectionKind,
    request?: ConversationCollectionRequest
  ) => Promise<ConversationCollectionPage>
  // The corpus reads, optional for the same reason the mutations above are: the defaults
  // bind them to the real searches, so a host opts out by omission.
  loadFile?: (projectId: number, fileId: number, scope: 'workspace' | 'global') => Promise<ContentFile>
  loadText?: (projectId: number, docId: number, scope: 'workspace' | 'global') => Promise<ContentText>
  loadConnector?: (projectId: number, connectorId: number) => Promise<ContentConnector>
}

// Every action above acts on an open chat's brief, output or execution, and this module
// cannot know which chat that is. The caller that has one supplies the real ones; the
// default refuses honestly rather than calling a route that no longer exists.
const chatRequired = () => Promise.reject(new Error('This action needs an open chat.'))

// A host may omit any of these from its builder, but no panel ever meets the omission:
// each panel merges its host's object over defaultServices exactly once, and the defaults
// bind every one to the real API function. This alias says that totality at the type
// level, so the mount code reads as plain calls rather than guarding each one.
type ArtifactMutationKey =
  | 'createSection'
  | 'renameSection'
  | 'deleteSection'
  | 'reorderSections'
  | 'deleteProposal'
  | 'renameProposal'
  | 'editSection'
  | 'startProposalBuildRun'
  | 'retryProposalBuildRun'
  | 'cancelProposalBuildRun'
  | 'getLatestProposalBuildRun'
  | 'getProposalBuildRun'
  | 'updatePresentationSlide'
  | 'refinePresentationSlide'
  | 'refinePresentationDeck'
  | 'renamePresentation'
  | 'startPresentationBuildRun'
  | 'retryPresentationBuildRun'
  | 'cancelPresentationBuildRun'
  | 'getLatestPresentationBuildRun'
  | 'getPresentationBuildRun'
  | 'getPresentationMessages'
  | 'deleteDocument'
  | 'renameDocument'

type MergedPanelServices = ConversationPanelServices & {
  [K in ArtifactMutationKey]-?: NonNullable<ConversationPanelServices[K]>
}

// The one merge a panel performs over its host's object. The spread cannot prove that
// every optional mutation now has a value - the host's side of the union is still typed
// optional - so the totality alias is asserted here rather than at forty call sites.
function mergePanelServices(services: ConversationPanelServices): MergedPanelServices {
  return { ...defaultServices, ...services } as MergedPanelServices
}

// Exported for tests, which hold the defaults to their full expected surface.
export const defaultServices: ConversationPanelServices = {
  getBrief: chatRequired,
  correctBrief: chatRequired,
  executeBrief: chatRequired,
  getOutputStaleness: chatRequired,
  cancelExecution: chatRequired,
  retryExecution: chatRequired,
  redraftOutput: chatRequired,
  getProposal,
  getPresentation,
  getDocument,
  exportProposal,
  exportPresentation,
  exportDocument,
  getSectionMessages,
  selectSectionVersion,
  deselectSectionVersion,
  getPresentationSlideMessages,
  selectPresentationSlideVersion,
  deselectPresentationSlideVersion,
  createSection,
  renameSection,
  deleteSection,
  reorderSections,
  deleteProposal,
  renameProposal,
  editSection,
  startProposalBuildRun,
  retryProposalBuildRun,
  cancelProposalBuildRun,
  getLatestProposalBuildRun,
  getProposalBuildRun,
  updatePresentationSlide,
  refinePresentationSlide,
  refinePresentationDeck,
  renamePresentation,
  startPresentationBuildRun,
  retryPresentationBuildRun,
  cancelPresentationBuildRun,
  getLatestPresentationBuildRun,
  getPresentationBuildRun,
  getPresentationMessages,
  deleteDocument,
  renameDocument,
  getSourceSummaries: fetchSourceSummaries,
  loadFactDocuments: loadConversationDocuments,
  loadCollection: loadConversationCollection,
  loadFile: loadContentFile,
  loadText: loadContentText,
  loadConnector: loadContentConnector
}

type EditDraft = {
  key: string
  label: string
  kind: 'text' | 'integer'
  value: string
}

const provenanceLabels: Record<FactSpineEntry['provenance'], string> = {
  'user-stated': 'User stated',
  'model-inferred': 'Model inferred',
  'validated-computation': 'Validated computation',
  'workspace-state': 'Workspace state',
  'safe-default': 'Safe default',
  'conversation-cited': 'Cited in this chat'
}

const stalenessReasons: Record<string, string> = {
  'brief-values-changed': 'The brief values changed after this output was written.',
  'document-changed': 'A source document changed after this output was written.',
  'document-unavailable': 'A source document is no longer available.'
}

// The design reference sizes the document canvas as clamp(440px,52vw,960px) against a
// chat column of at least 360px, and gives the sources panel a fixed 340px. The canvas
// is the one a reader resizes, so it is the one that persists a width.
const panelWidthStorageKey = 'snowvault.conversation.panel-width.v1'
const defaultPanelWidth = 640
const minPanelWidth = 440
const maxPanelWidth = 960
const minConversationWidth = 360
export const chatInspectorWidth = 340

function clampPanelWidth(width: number, maximum = maxPanelWidth): number {
  return Math.min(Math.max(width, minPanelWidth), maximum)
}

function readPanelWidth(): number {
  if (typeof window === 'undefined') return defaultPanelWidth
  try {
    const stored = window.localStorage.getItem(panelWidthStorageKey)
    // The reference expresses the default as 52vw. A persisted setting that starts at a
    // different number on every screen is a worse setting than one that starts at the
    // same place, so the viewport bounds the width through the maximum below instead.
    if (stored === null) return defaultPanelWidth
    const parsed = Number(stored)
    return Number.isFinite(parsed) ? clampPanelWidth(parsed) : defaultPanelWidth
  } catch {
    return defaultPanelWidth
  }
}

function persistPanelWidth(width: number): void {
  try {
    window.localStorage.setItem(panelWidthStorageKey, String(Math.round(width)))
  } catch {
    // Browser storage can be unavailable without making the panel unusable.
  }
}

function useDesktopPanelWidth() {
  const panelRef = useRef<HTMLElement | null>(null)
  const [preferredWidth, setPreferredWidth] = useState(readPanelWidth)
  const [containerWidth, setContainerWidth] = useState(() =>
    typeof window === 'undefined' ? defaultPanelWidth + minConversationWidth : window.innerWidth
  )
  const desktopSpace = containerWidth >= minPanelWidth + minConversationWidth
  const effectiveMaximum = Math.max(minPanelWidth, Math.min(maxPanelWidth, containerWidth - minConversationWidth))
  const width = clampPanelWidth(preferredWidth, effectiveMaximum)
  const liveWidth = useRef(width)

  useEffect(() => {
    liveWidth.current = width
  }, [width])

  useEffect(() => {
    const container = panelRef.current?.parentElement
    const update = () => {
      const measured = container?.getBoundingClientRect().width ?? 0
      setContainerWidth(measured > 0 ? measured : window.innerWidth)
    }
    update()
    window.addEventListener('resize', update)
    const hasContainer = container !== null && container !== undefined
    const observer = hasContainer && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    if (observer !== null && container !== null && container !== undefined) observer.observe(container)
    return () => {
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [])

  const resizeBy = useCallback(
    (pointerDelta: number) => {
      const next = clampPanelWidth(liveWidth.current - pointerDelta, effectiveMaximum)
      liveWidth.current = next
      setPreferredWidth(next)
    },
    [effectiveMaximum]
  )
  const commit = useCallback(() => persistPanelWidth(liveWidth.current), [])

  return { panelRef, width, effectiveMaximum, desktopSpace, resizeBy, commit }
}

function useNarrowPanel(): boolean {
  const query = '(max-width: 767px)'
  const [narrow, setNarrow] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(query).matches))
  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setNarrow(media.matches)
    media.addEventListener('change', update)
    update()
    return () => media.removeEventListener('change', update)
  }, [])
  return narrow
}

function canvasDataAttributes(canvas?: WorkspaceCanvasIdentity) {
  return {
    'data-testid': 'workspace-canvas-slot',
    'data-canvas': canvas?.canvas ?? 'closed',
    ...(canvas?.canvas === 'proposal'
      ? { 'data-proposal-id': canvas.proposalId, 'data-section-id': canvas.sectionId }
      : {}),
    ...(canvas?.canvas === 'presentation' ? { 'data-presentation-id': canvas.presentationId } : {}),
    ...(canvas?.canvas === 'presentation' && canvas.slideId !== undefined ? { 'data-slide-id': canvas.slideId } : {}),
    ...(canvas?.canvas === 'collection' ? { 'data-collection': canvas.collection } : {})
  }
}

function inspectorDataAttributes(inspector?: ChatInspectorIdentity) {
  return {
    'data-testid': 'chat-inspector-slot',
    'data-inspector': inspector?.inspector ?? 'closed',
    ...(inspector?.inspector === 'facts' ? { 'data-brief-id': inspector.briefId } : {}),
    ...(inspector?.inspector === 'sources' ? { 'data-message-id': inspector.messageId } : {}),
    ...(inspector?.inspector === 'activity' ? { 'data-origin-message-id': inspector.originMessageId } : {})
  }
}

// Every surfaced inspector or artifact-panel failure falls back to this line when the
// thrown value carries no usable message.
const loadFailureFallback = 'This information could not be loaded.'

function isVersionConflict(error: unknown): boolean {
  const message = failureLine(error, loadFailureFallback).toLowerCase()
  return message.includes('conflict') || message.includes('version')
}

function formatObservedTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp)
}

function SourceRows({
  docIds,
  services,
  capturedFileNames = new Map()
}: {
  docIds: Array<number>
  services: ConversationPanelServices
  capturedFileNames?: ReadonlyMap<number, string>
}) {
  const details = useSourceDetails(docIds, services.getSourceSummaries)
  const loadDetails = details.load
  useEffect(() => loadDetails(), [loadDetails])

  if (details.errorMessage !== null) {
    return (
      <div className="py-2 text-sm text-destructive">
        <p role="alert">{details.errorMessage}</p>
        {details.retryable && (
          <Button type="button" size="sm" variant="outline" className="mt-2" onClick={details.retry}>
            Retry source details
          </Button>
        )}
      </div>
    )
  }
  if (details.loading || details.summaries === null) {
    return (
      <p className="py-2 text-sm text-muted-foreground" role="status">
        Loading source details
      </p>
    )
  }
  if (details.summaries.length === 0) {
    return <p className="py-2 text-sm text-muted-foreground">No source details are attached.</p>
  }
  return (
    <div role="list" className="divide-y">
      {details.summaries.map((summary) => (
        <SourceSummaryRow
          key={summary.docId}
          summary={summary}
          capturedFileName={capturedFileNames.get(summary.docId)}
        />
      ))}
    </div>
  )
}

function FactObservation({ entry }: { entry: FactSpineEntry }) {
  if (entry.source !== undefined) {
    return (
      <p className="text-xs text-muted-foreground">
        Message {entry.source.messageId}, observed {formatObservedTime(entry.source.updatedAtTsMs)}
      </p>
    )
  }
  if (entry.rawValue.type === 'document-reference') {
    return (
      <p className="text-xs text-muted-foreground">
        Source observed {formatObservedTime(entry.rawValue.document.updatedAtTsMs)}
      </p>
    )
  }
  if (entry.rawValue.type === 'document-references') {
    return (
      <p className="text-xs text-muted-foreground">{entry.rawValue.documents.length} source observations retained</p>
    )
  }
  if (entry.rawValue.type === 'artifact-reference') {
    return (
      <p className="text-xs text-muted-foreground">
        Proposal observed {formatObservedTime(entry.rawValue.artifact.observation.proposalUpdatedAtTsMs)}, section
        observed {formatObservedTime(entry.rawValue.artifact.observation.sectionUpdatedAtTsMs)}
      </p>
    )
  }
  return null
}

function FactSourceDisclosure({ entry, services }: { entry: FactSpineEntry; services: ConversationPanelServices }) {
  const [expanded, setExpanded] = useState(false)
  const documents =
    entry.rawValue.type === 'document-reference'
      ? [entry.rawValue.document]
      : entry.rawValue.type === 'document-references'
        ? entry.rawValue.documents
        : []
  if (documents.length === 0) return null
  const docIds = documents.map((document) => document.docId)
  const capturedFileNames = new Map(documents.map((document) => [document.docId, document.fileName]))
  return (
    <div className="mt-2">
      <button
        type="button"
        className="border-b border-dotted border-current text-xs text-foreground"
        aria-expanded={expanded}
        aria-label={`Source details for ${entry.label}`}
        onClick={() => setExpanded((current) => !current)}
      >
        Source details
      </button>
      {expanded && (
        <div className="mt-2 rounded-md border px-3 py-1">
          <SourceRows docIds={docIds} services={services} capturedFileNames={capturedFileNames} />
        </div>
      )}
    </div>
  )
}

function FactProvenance({
  entry,
  onNavigate
}: {
  entry: FactSpineEntry
  onNavigate: (identity: ChatInspectorIdentity) => void
}) {
  const [expanded, setExpanded] = useState(false)
  if (entry.provenance !== 'validated-computation') {
    return (
      <>
        <p className="mt-1 text-xs text-muted-foreground">{provenanceLabels[entry.provenance]}</p>
        <FactObservation entry={entry} />
      </>
    )
  }
  return (
    <div className="mt-1 text-xs text-muted-foreground">
      <button
        type="button"
        className="border-b border-dotted border-current text-left text-foreground"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        Validated computation details
      </button>
      {expanded && (
        <div className="mt-2 space-y-1">
          <FactObservation entry={entry} />
          {entry.source !== undefined && (
            <button
              type="button"
              className="font-medium text-primary underline-offset-4 hover:underline"
              onClick={() => onNavigate({ inspector: 'sources', messageId: entry.source!.messageId })}
            >
              Open source message {entry.source.messageId}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

type SafeMessageEvidence = {
  documentId: number
  claimStartByte: number
  claimEndByte: number
  claim: string
  excerpt: string
  chunkId: number
  sourceStartByte: number
  sourceEndByte: number
}

function safeMessageEvidence(
  refs: unknown,
  content: string,
  citationIDs: ReadonlyArray<number>
): Array<SafeMessageEvidence> {
  if (typeof refs !== 'object' || refs === null || Array.isArray(refs)) return []
  const values = (refs as Record<string, unknown>)['evidence']
  if (!Array.isArray(values) || values.length > 24) return []
  const citations = new Set(citationIDs)
  const contentBytes = new TextEncoder().encode(content)
  const decoder = new TextDecoder('utf-8', { fatal: true })
  const seen = new Set<string>()
  const perClaim = new Map<string, number>()
  const result: Array<SafeMessageEvidence> = []
  for (const value of values) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) continue
    const evidence = value as Record<string, unknown>
    const documentId = evidence['documentId']
    const claimStartByte = evidence['claimStartByte']
    const claimEndByte = evidence['claimEndByte']
    const excerpt = evidence['excerpt']
    const chunkId = evidence['chunkId']
    const sourceStartByte = evidence['sourceStartByte']
    const sourceEndByte = evidence['sourceEndByte']
    const integers = [documentId, claimStartByte, claimEndByte, chunkId, sourceStartByte, sourceEndByte]
    if (!integers.every((item) => typeof item === 'number' && Number.isSafeInteger(item))) continue
    if (
      (documentId as number) <= 0 ||
      !citations.has(documentId as number) ||
      (claimStartByte as number) < 0 ||
      (claimEndByte as number) <= (claimStartByte as number) ||
      (claimEndByte as number) > contentBytes.byteLength ||
      (claimEndByte as number) - (claimStartByte as number) > 1024 ||
      (chunkId as number) < 0 ||
      (sourceStartByte as number) < 0 ||
      (sourceEndByte as number) <= (sourceStartByte as number) ||
      typeof excerpt !== 'string'
    ) {
      continue
    }
    const excerptBytes = new TextEncoder().encode(excerpt)
    if (
      excerpt.trim() === '' ||
      excerptBytes.byteLength > 480 ||
      (sourceEndByte as number) - (sourceStartByte as number) !== excerptBytes.byteLength
    ) {
      continue
    }
    let claim: string
    try {
      claim = decoder.decode(contentBytes.slice(claimStartByte as number, claimEndByte as number))
    } catch {
      continue
    }
    if (claim.trim() === '') continue
    const claimKey = `${claimStartByte}:${claimEndByte}`
    const dedupKey = `${documentId}:${claimKey}`
    if (seen.has(dedupKey) || (perClaim.get(claimKey) ?? 0) >= 3) continue
    seen.add(dedupKey)
    perClaim.set(claimKey, (perClaim.get(claimKey) ?? 0) + 1)
    result.push({
      documentId: documentId as number,
      claimStartByte: claimStartByte as number,
      claimEndByte: claimEndByte as number,
      claim,
      excerpt,
      chunkId: chunkId as number,
      sourceStartByte: sourceStartByte as number,
      sourceEndByte: sourceEndByte as number
    })
  }
  return result
}

function nextRequestID(cache: MutableRefObject<{ signature: string; id: string } | undefined>, signature: string) {
  if (cache.current?.signature === signature) return cache.current.id
  const id = crypto.randomUUID()
  cache.current = { signature, id }
  return id
}

function outputAuthoritySignature(output: ConversationStaleness): string {
  return JSON.stringify({
    state: output.state,
    reasons: output.reasons,
    briefId: output.briefId ?? null,
    linkedVersion: output.linkedVersion ?? null,
    currentVersion: output.currentVersion ?? null
  })
}

export type CanvasExportTarget =
  | { kind: 'proposal'; proposalId: number; proposal: Proposal }
  | { kind: 'presentation'; presentationId: number; presentation: Presentation }
  | { kind: 'document'; documentId: number; revision: DocumentRevision }

const canvasExportLabels: Record<CanvasExportTarget['kind'], string> = {
  proposal: 'Export DOCX',
  presentation: 'Export PPTX',
  document: 'Export DOCX'
}

// Read from the loaded artifact, so a reader can see why an export is not offered rather
// than meeting a dead control. It does not replace the server's own refusal: the server
// checks the same conditions on its own authority, and when it says no its message is
// what reaches the screen. Short enough to sit on one line beside the button.
function canvasExportRefusal(target: CanvasExportTarget): string | undefined {
  if (target.kind === 'proposal') {
    const sections = target.proposal.sections
    if (sections.length === 0) return 'This proposal has no sections yet.'
    const unready = sections.filter((section) => section.state !== 'ready').length
    if (unready === 0) return undefined
    return `${unready} of ${sections.length} sections ${unready === 1 ? 'is' : 'are'} not ready yet.`
  }
  if (target.kind === 'presentation') {
    // Positions count from one the way the canvas numbers the deck, because the number
    // the reason names has to be the number the reader can see. Stored ordinals start at
    // zero and can leave gaps, so neither the ordinal nor the ordinal plus one is it.
    const slides = [...target.presentation.slides]
      .sort((left, right) => left.ordinal - right.ordinal || left.slideId - right.slideId)
      .map((slide, index) => ({ slide, position: index + 1 }))
    if (slides.length === 0) return 'This deck has no slides yet.'
    const unready = slides.find((candidate) => candidate.slide.state !== 'ready')
    if (unready !== undefined) return `Slide ${unready.position}, ${unready.slide.title}, is not ready yet.`
    const overflowing = slides.find((candidate) => candidate.slide.fit.status === 'overflow')
    if (overflowing === undefined) return undefined
    return `Slide ${overflowing.position}, ${overflowing.slide.title}, does not fit.`
  }
  return target.revision.content.trim() === '' ? 'This revision is empty.' : undefined
}

// What a warning is about. Ids rather than object identity: a successful export calls
// onExported, the panel reloads, and the same artifact comes back as a new object whose
// warnings are the ones just earned. A different artifact, or a different revision of
// one, is a different subject and starts clean.
function canvasExportTargetKey(target: CanvasExportTarget): string {
  if (target.kind === 'proposal') return `proposal:${target.proposalId}`
  if (target.kind === 'presentation') return `presentation:${target.presentationId}`
  return `document:${target.documentId}:${target.revision.versionId}`
}

// What a failure is about, which is narrower: a refusal describes the artifact the server
// read, so a reload that delivers that artifact again retires it even though the ids have
// not moved. The loaded artifact object is the subject, not the target wrapper the panel
// rebuilds on every render, and not a timestamp: approving or editing a section writes
// proposal_section only, and the proposal row's updated_at trigger does not cascade from a
// child, so a timestamp would never move for the transition that retires the refusal. The
// inner object is held in panel state and replaced exactly when a reload lands.
function canvasExportSubject(target: CanvasExportTarget): Proposal | Presentation | DocumentRevision {
  if (target.kind === 'proposal') return target.proposal
  if (target.kind === 'presentation') return target.presentation
  return target.revision
}

// The name comes back with every export and is carried rather than dropped: it is what
// makes the click a download instead of a navigation, and it is the name the file lands
// under.
async function runCanvasExport(
  projectId: number,
  target: CanvasExportTarget,
  services: ConversationPanelServices
): Promise<{ downloadUrl: string; fileName: string; warnings: Array<string> }> {
  if (target.kind === 'proposal') {
    const exported = await services.exportProposal(projectId, target.proposalId)
    return {
      downloadUrl: exported.downloadUrl,
      fileName: exported.fileName,
      warnings: exported.warnings.map((warning) => warning.message)
    }
  }
  if (target.kind === 'presentation') {
    const exported = await services.exportPresentation(projectId, target.presentationId)
    return { downloadUrl: exported.downloadUrl, fileName: exported.fileName, warnings: [] }
  }
  // The revision on screen rather than the document's current one: a reader exporting
  // what they are reading must not be handed a different revision.
  const exported = await services.exportDocument(projectId, target.documentId, target.revision.versionId)
  return { downloadUrl: exported.downloadUrl, fileName: exported.fileName, warnings: [] }
}

// A detached anchor, clicked once and removed, which is what the legacy proposal route
// does. Assigning the location instead would unload the SPA to fetch a file it is not
// navigating to. The name states the intent and binds for the same-origin, blob: and data:
// cases; cross-origin it is ignored, and the download works there because the object store
// presigns every export with Content-Disposition: attachment, so the response is never
// rendered. Same tab on purpose. The click happens in the continuation of the export
// request rather than in the reader's own gesture, so it can outlive the transient
// activation that gesture granted, and a click asking for a new top-level browsing context
// without activation is exactly what a popup blocker stops: the reader would get a blocked
// indicator and no file, silently. An attachment response also opens a tab that closes
// again at once, so every export would flash one.
function startCanvasDownload(downloadUrl: string, fileName: string): void {
  const anchor = document.createElement('a')
  anchor.href = downloadUrl
  anchor.rel = 'noopener'
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

// The no-op reload a document export passes as its onExported. A stable identity,
// because useCanvasExport re-runs its notify effect whenever the identity changes.
const noExportReload = (): void => undefined

type CanvasExportController = {
  label: string
  pending: boolean
  refusal?: string
  failure?: string
  warnings: Array<string>
  start: () => void
}

// One entry per invocation rather than one slot per hook. Two exports can be in flight at
// once across a switch of artifact, and a key cannot tell them apart: only a token can say
// which export a completion is, so that pending survives leaving and returning, and a
// completion can never retire an export that is not its own.
type CanvasExportFlight = { token: number; key: string }

type CanvasExportState = {
  // What is on screen. Kept true by the reset during render below, which is what lets a
  // completion arriving from an artifact the reader has left be told from a current one.
  key: string
  subject: Proposal | Presentation | DocumentRevision
  failure?: string
  // Warnings keep the ids key, because they have to survive the reload their own success
  // triggers, which arrives as a new artifact object under the same ids.
  warningsKey: string
  warnings: Array<string>
  flights: Array<CanvasExportFlight>
  // The token of the last success that belonged to the artifact on screen. Tokens are
  // unique, so the effect below turns each new value into exactly one onExported call and
  // a success the reader has navigated away from into none.
  exported: number
}

// A controller and two placements, which is the shape the other hooks here have. Nothing
// it produces is a message in the header's action row: that row holds controls, and a
// sentence set among them reads as a label for whichever one it lands beside.
export function useCanvasExport({
  projectId,
  target,
  services,
  onExported
}: {
  projectId: number
  target: CanvasExportTarget
  services: ConversationPanelServices
  onExported: () => void
}): CanvasExportController {
  const key = canvasExportTargetKey(target)
  const subject = canvasExportSubject(target)
  const [state, setState] = useState<CanvasExportState>({
    key,
    subject,
    warningsKey: key,
    warnings: [],
    flights: [],
    exported: 0
  })
  const flightToken = useRef(0)
  // A duplicate export is refused here rather than by a disabled control, because the
  // control has to stay focusable while the work runs: disabling the focused element runs
  // the browser's unfocusing steps, focus lands on the body, and re-enabling does not
  // bring it back. A ref rather than the rendered flights, so two clicks landing in one
  // batch cannot both start.
  const flightKeys = useRef(new Set<string>())
  // The panel hands a fresh callback down on every render, so the notification effect
  // cannot depend on its identity without firing a reload every time it renders.
  const notify = useRef(onExported)
  useEffect(() => {
    notify.current = onExported
  }, [onExported])
  useEffect(() => {
    if (state.exported !== 0) notify.current()
  }, [state.exported])
  // Reset during render, which is how React drops state a prop change has invalidated. An
  // effect cannot: it would paint the previous artifact's failure once beside the new
  // artifact's button, and this project's lint refuses setState in an effect body anyway.
  // Warnings and flights carry over; the failure is what the replaced object invalidates.
  if (state.key !== key || state.subject !== subject) {
    setState({
      key,
      subject,
      warningsKey: state.warningsKey,
      warnings: state.warnings,
      flights: state.flights,
      exported: state.exported
    })
  }
  const refusal = canvasExportRefusal(target)
  const start = () => {
    if (flightKeys.current.has(key)) return
    flightKeys.current.add(key)
    flightToken.current += 1
    const token = flightToken.current
    setState((prev) => ({ ...prev, flights: [...prev.flights, { token, key }] }))
    void (async () => {
      try {
        const exported = await runCanvasExport(projectId, target, services)
        // The file was asked for, so it arrives even if the reader has moved on since.
        startCanvasDownload(exported.downloadUrl, exported.fileName)
        setState((prev) => {
          const flights = prev.flights.filter((flight) => flight.token !== token)
          // prev.key is the artifact on screen, because the reset above maintains it. An
          // unguarded write would hand this export's finished state to whatever the reader
          // is looking at now, and tell the panel to reload it.
          if (prev.key !== key) return { ...prev, flights }
          return { ...prev, flights, warningsKey: key, warnings: exported.warnings, exported: token }
        })
      } catch (exportError) {
        const message =
          exportError instanceof Error && exportError.message.trim() !== ''
            ? exportError.message
            : 'The export did not finish.'
        setState((prev) => {
          const flights = prev.flights.filter((flight) => flight.token !== token)
          // Guarded for the same reason, and against a worse outcome: writing this message
          // over the one the reader is reading would erase a refusal the server made about
          // the artifact in front of them, and the reset above would then drop it.
          if (prev.subject !== subject) return { ...prev, flights }
          return { ...prev, flights, failure: message }
        })
      } finally {
        flightKeys.current.delete(key)
      }
    })()
  }
  const failure = state.subject === subject ? state.failure : undefined
  return {
    label: canvasExportLabels[target.kind],
    pending: state.flights.some((flight) => flight.key === key),
    ...(refusal === undefined ? {} : { refusal }),
    ...(failure === undefined ? {} : { failure }),
    warnings: state.warningsKey === key ? state.warnings : [],
    start
  }
}

// A constant id rather than useId: the button and the text describing it are separate
// components with no shared render, and a canvas mounts one export control at a time.
// NewPresentationForm wires its field errors to their inputs the same way.
const canvasExportNoticeId = 'canvas-export-notice'
const canvasExportRefusalId = 'canvas-export-refusal'

// Whichever of the two the controller is currently saying something in, so a description
// is never built out of an element that is empty.
function canvasExportDescribedBy(controller: CanvasExportController): string | undefined {
  const described: Array<string> = []
  if (controller.refusal !== undefined) described.push(canvasExportRefusalId)
  if (controller.failure !== undefined || controller.warnings.length > 0) described.push(canvasExportNoticeId)
  return described.length === 0 ? undefined : described.join(' ')
}

export function CanvasExportControl({ controller }: { controller: CanvasExportController }): ReactNode {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      // A refusal is not transient, so it disables. Pending is announced instead: taking
      // the disabled attribute to a focused control runs the browser's unfocusing steps,
      // and a reader who pressed Export loses their place for the length of a render that
      // takes seconds. The duplicate click the attribute used to stop is refused by the
      // controller.
      disabled={controller.refusal !== undefined}
      aria-disabled={controller.pending ? true : undefined}
      className="aria-disabled:opacity-50"
      // The reason is a separate node wherever it sits, so the button has to point at it:
      // a disabled control whose reason a screen reader cannot reach is the bare control
      // again. Two nodes can be describing it, and the refusal comes first because it is
      // the one that explains the disabling.
      aria-describedby={canvasExportDescribedBy(controller)}
      onClick={controller.start}
    >
      {controller.pending ? (
        <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      ) : (
        <Download className="size-4" aria-hidden="true" />
      )}
      {controller.pending ? 'Exporting...' : controller.label}
    </Button>
  )
}

// The reason a dead control is dead, rendered on the sticky header's own line so that it
// stays with the control it explains. Beneath the header it scrolled away while the
// disabled button stayed, which is the bare dead control the design forbids, restored by
// scroll position: measured in a browser at y: 75 unscrolled and y: -29 once scrolled into
// the artifact. Not always mounted, because nothing here is announced and an absent
// refusal should take no height. Horizontal padding comes from the header, so the sentence
// starts under the title rather than indented past it.
export function CanvasExportRefusal({ controller }: { controller: CanvasExportController }): ReactNode {
  if (controller.refusal === undefined) return null
  return (
    <p id={canvasExportRefusalId} className="pt-3 text-xs text-muted-foreground">
      {controller.refusal}
    </p>
  )
}

// What an export that already ran has to report, which is not why a control is disabled:
// a failure and its warnings are the result the reader is standing at the control for, so
// they stay in the scrolling body rather than taking pinned height from the artifact for
// as long as the reader keeps reading. Always mounted, and each line carries its own
// padding, so a canvas with nothing to report gives this no height at all and needs no
// wrapper to collapse. Mounted rather than
// conditional because of the live region below: a region inserted into the DOM with its
// content already inside it is not announced, which would have made the first export of an
// artifact the one export that said nothing.
export function CanvasExportNotice({ controller }: { controller: CanvasExportController }): ReactNode {
  return (
    <div id={canvasExportNoticeId} className="text-xs">
      {controller.failure !== undefined && (
        <p role="alert" className="px-5 pt-3 text-destructive">
          {controller.failure}
        </p>
      )}
      {/* Announced, because an export that succeeded with warnings was otherwise silent to
          a reader who cannot see the notice appear: the file downloaded and nothing said
          the cover page had lost its logo. Politely rather than as an alert, since the
          export did succeed, and around the list rather than on it so the warnings keep
          their list semantics. */}
      <div role="status">
        {controller.warnings.length > 0 && (
          <ul className="space-y-1 px-5 pt-3 text-muted-foreground">
            {/* Keyed by position as well as text: one export can report the same warning
                about two sections, and keying on the message alone would render it once. */}
            {controller.warnings.map((warning, position) => (
              <li key={`${position}:${warning}`}>{warning}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

const canvasCopyConfirmationMs = 1_500

// One item carrying both flavours, so a paste into a document keeps the headings and a
// paste into a plain field still gets the prose.
async function copyRevisionToClipboard(text: string, html: string): Promise<void> {
  if (typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' })
        })
      ])
      return
    } catch {
      // A browser that refuses the rich item can still take the prose, which is the part
      // a reader cannot retype.
    }
  }
  await navigator.clipboard.writeText(text)
}

type CanvasCopyController = {
  copied: boolean
  failure?: string
  copy: () => void
}

export function useCanvasCopy({
  revision,
  renderedRef
}: {
  revision: DocumentRevision
  renderedRef: RefObject<HTMLElement | null>
}): CanvasCopyController {
  const [outcome, setOutcome] = useState<{ versionId: number; state: 'copied' | 'failed' }>()
  // Reset rather than shadowed, as with the export failure: reading another revision and
  // coming back must neither restore the message nor restart the confirmation's timer.
  if (outcome !== undefined && outcome.versionId !== revision.versionId) setOutcome(undefined)
  const current = outcome?.versionId === revision.versionId ? outcome.state : undefined
  useEffect(() => {
    if (current !== 'copied') return undefined
    const timer = window.setTimeout(() => setOutcome(undefined), canvasCopyConfirmationMs)
    return () => window.clearTimeout(timer)
  }, [current])
  const copy = () => {
    void (async () => {
      try {
        await copyRevisionToClipboard(revision.content, renderedRef.current?.innerHTML ?? '')
        setOutcome({ versionId: revision.versionId, state: 'copied' })
      } catch {
        setOutcome({ versionId: revision.versionId, state: 'failed' })
      }
    })()
  }
  return {
    copied: current === 'copied',
    ...(current === 'failed' ? { failure: 'This revision could not be copied.' } : {}),
    copy
  }
}

const canvasCopyNoticeId = 'canvas-copy-notice'

export function CanvasCopyControl({ controller }: { controller: CanvasCopyController }): ReactNode {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      aria-describedby={controller.failure === undefined ? undefined : canvasCopyNoticeId}
      onClick={controller.copy}
    >
      {controller.copied ? (
        <Check className="size-4" aria-hidden="true" />
      ) : (
        <Copy className="size-4" aria-hidden="true" />
      )}
      Copy
    </Button>
  )
}

export function CanvasCopyNotice({ controller }: { controller: CanvasCopyController }): ReactNode {
  // Always mounted, empty until there is something to say, for the reason CanvasExportNotice
  // is: a live region inserted with its text already inside it is not announced, so a region
  // that appears only once there is a confirmation announces nothing the first time. Each
  // line carries its own padding for the same reason it does there: an always-mounted notice
  // inside a padded wrapper holds that padding open on a canvas with nothing to report. The
  // failure stays an alert and the confirmation stays polite.
  return (
    <div id={canvasCopyNoticeId} className="text-xs">
      {controller.failure !== undefined && (
        <p role="alert" className="px-5 pt-3 text-destructive">
          {controller.failure}
        </p>
      )}
      <div role="status">{controller.copied && <p className="px-5 pt-3 text-muted-foreground">Copied</p>}</div>
    </div>
  )
}

// The surfaces mostly carry their own scope by construction rather than by a switch: an
// artifact on the canvas is always the workspace's, the inspector is always the open
// chat's. The lines stay because a reader looking at two columns still has to know which
// is which, and because they name the thing rather than only its kind.
function workspaceScope(projectName: string): string {
  // The scope says who owns this, and for an artifact the owner is the workspace whatever
  // chat happened to start it. Provenance used to ride on the end of this string as a
  // `· from` suffix, where it was both the answer to "why is this next to that chat?" and
  // the least visible text on screen. It has its own line now: see CanvasProvenanceLine.
  return `Workspace · ${projectName}`
}

// The one thing on the canvas the workspace does not own. A file or a text can be the
// organisation's, read by every workspace and belonging to none, so it cannot carry a
// line naming this one. There is no name to put beside it either: the canvas is told
// which workspace it is open in, never which organisation owns it, so the line says what
// is true without one.
const organisationScope = 'Organisation content'

function chatScope(chatTitle?: string): string {
  return chatTitle === undefined ? 'This chat' : `This chat · ${chatTitle}`
}

function OutputBasis({
  output,
  onRedraft,
  redrafting,
  kept,
  onKeep
}: {
  output: ConversationStaleness
  onRedraft?: () => void
  redrafting?: boolean
  kept?: boolean
  onKeep?: () => void
}) {
  if (kept) return null
  const stateCopy = {
    current: 'Current. This output matches the current brief and available sources.',
    stale: 'Basis changed. This output may no longer match the current brief or sources.',
    untracked: 'Untracked. Snowvault cannot compare this output with a durable brief.'
  }[output.state]
  const Icon = output.state === 'current' ? CheckCircle2 : output.state === 'stale' ? AlertTriangle : CircleDashed
  return (
    <section className="border-y bg-muted/25 px-5 py-4" aria-label={`Output ${output.messageId} status`}>
      <div className="flex gap-3">
        <Icon
          className={cn('mt-0.5 size-4 shrink-0', output.state === 'stale' ? 'text-warning' : 'text-muted-foreground')}
          aria-hidden="true"
        />
        <div className="min-w-0 space-y-2 text-sm">
          <p className="font-medium">{stateCopy}</p>
          {output.reasons.map((reason) => (
            <p key={reason} className="text-muted-foreground">
              {stalenessReasons[reason] ?? 'The output basis could not be verified.'}
            </p>
          ))}
          {output.state === 'stale' && onRedraft !== undefined && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" size="sm" onClick={onRedraft} disabled={redrafting}>
                {redrafting ? 'Starting re-draft' : 'Re-draft'}
              </Button>
              {onKeep !== undefined && (
                <Button type="button" size="sm" variant="ghost" onClick={onKeep}>
                  Keep as is
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function OutputDecision({
  projectId,
  output,
  services
}: {
  projectId: number
  output: ConversationStaleness
  services: ConversationPanelServices
}) {
  const [pending, setPending] = useState(false)
  const [keptSignature, setKeptSignature] = useState<string>()
  const [error, setError] = useState<string>()
  const request = useRef<{ signature: string; id: string } | undefined>(undefined)
  const authoritySignature = outputAuthoritySignature(output)
  const redraft = async () => {
    if (
      output.briefId === null ||
      output.briefId === undefined ||
      output.currentVersion === null ||
      output.currentVersion === undefined
    ) {
      setError('This output does not have a current brief to re-draft from.')
      return
    }
    const signature = `${output.messageId}:${authoritySignature}`
    const clientRequestId = nextRequestID(request, signature)
    setPending(true)
    setError(undefined)
    try {
      await services.redraftOutput(projectId, output.messageId, {
        clientRequestId,
        briefId: output.briefId,
        expectedVersion: output.currentVersion
      })
    } catch (redraftError) {
      setError(failureLine(redraftError, loadFailureFallback))
    } finally {
      setPending(false)
    }
  }
  return (
    <>
      <OutputBasis
        output={output}
        kept={keptSignature === authoritySignature}
        onKeep={() => setKeptSignature(authoritySignature)}
        onRedraft={() => void redraft()}
        redrafting={pending}
      />
      {error !== undefined && <p className="px-5 py-3 text-sm text-destructive">{error}</p>}
    </>
  )
}

type ReplacementValue = Extract<
  FactSpineEntry['rawValue'],
  { type: 'document-reference' | 'document-references' | 'artifact-reference' }
>

type ObservedDocumentOption = ConversationComposerOption & { updatedAtTsMs: number }

function isObservedDocument(option: ConversationComposerOption): option is ObservedDocumentOption {
  return (
    option.updatedAtTsMs !== undefined &&
    Number.isSafeInteger(option.updatedAtTsMs) &&
    option.updatedAtTsMs > 0 &&
    option.label.trim() !== ''
  )
}

function isValidArtifactObservation(
  artifact: Extract<ReplacementValue, { type: 'artifact-reference' }>['artifact']
): boolean {
  return (
    Number.isSafeInteger(artifact.proposalId) &&
    artifact.proposalId > 0 &&
    Number.isSafeInteger(artifact.sectionId) &&
    artifact.sectionId > 0 &&
    Number.isSafeInteger(artifact.observation.proposalUpdatedAtTsMs) &&
    artifact.observation.proposalUpdatedAtTsMs > 0 &&
    Number.isSafeInteger(artifact.observation.sectionUpdatedAtTsMs) &&
    artifact.observation.sectionUpdatedAtTsMs > 0
  )
}

function artifactReplacementCandidates(state: ConversationState, hydratedBrief: ConversationBrief) {
  const candidates = new Map<string, Extract<ReplacementValue, { type: 'artifact-reference' }>['artifact']>()
  const briefs = [...state.briefs.filter((brief) => brief.briefId !== hydratedBrief.briefId), hydratedBrief]
  for (const brief of briefs) {
    for (const slot of brief.slots) {
      if (slot.value.type !== 'artifact-reference') continue
      const artifact = slot.value.artifact
      if (!isValidArtifactObservation(artifact)) continue
      const key = `${artifact.proposalId}:${artifact.sectionId}:${artifact.observation.proposalUpdatedAtTsMs}:${artifact.observation.sectionUpdatedAtTsMs}`
      candidates.set(key, artifact)
    }
  }
  return [...candidates.values()].sort(
    (left, right) => left.proposalId - right.proposalId || left.sectionId - right.sectionId
  )
}

function FactReplacementEditor({
  projectId,
  entry,
  hydratedBrief,
  state,
  services,
  pending,
  onCancel,
  onSave
}: {
  projectId: number
  entry: FactSpineEntry
  hydratedBrief: ConversationBrief
  state: ConversationState
  services: ConversationPanelServices
  pending: boolean
  onCancel: () => void
  onSave: (value: ReplacementValue) => void
}) {
  const [selectedDocuments, setSelectedDocuments] = useState<Array<ObservedDocumentOption>>([])
  const [selectedArtifactKey, setSelectedArtifactKey] = useState(() =>
    entry.rawValue.type === 'artifact-reference'
      ? `${entry.rawValue.artifact.proposalId}:${entry.rawValue.artifact.sectionId}:${entry.rawValue.artifact.observation.proposalUpdatedAtTsMs}:${entry.rawValue.artifact.observation.sectionUpdatedAtTsMs}`
      : ''
  )
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [search, setSearch] = useState('')
  const documentLoadKey = `${projectId}:${entry.key}:${loadAttempt}`
  const [documentLoad, setDocumentLoad] = useState<{
    key: string
    query: string
    items: Array<ObservedDocumentOption>
    page: number
    hasMore: boolean
    loading: boolean
    error?: true
  }>()
  const documentRequest = useRef(0)
  const needsDocuments = entry.rawValue.type === 'document-reference' || entry.rawValue.type === 'document-references'

  const loadDocumentPage = useCallback(
    (page: number, requestedSearch: string, replace: boolean) => {
      const request = documentRequest.current + 1
      documentRequest.current = request
      setDocumentLoad((current) => ({
        key: documentLoadKey,
        query: requestedSearch,
        items: replace || current?.key !== documentLoadKey || current.query !== requestedSearch ? [] : current.items,
        page: replace || current?.key !== documentLoadKey || current.query !== requestedSearch ? 0 : current.page,
        hasMore: false,
        loading: true
      }))
      void services
        .loadFactDocuments(projectId, { page, search: requestedSearch })
        .then((result) => {
          if (documentRequest.current !== request) return
          const loadedPage = Array.isArray(result) ? { items: result, hasMore: false } : result
          const observed = loadedPage.items.filter(isObservedDocument)
          setDocumentLoad((current) => {
            if (
              current?.key !== documentLoadKey ||
              current.query !== requestedSearch ||
              current.page !== (replace ? 0 : page - 1)
            ) {
              return current
            }
            const combined = replace ? observed : [...current.items, ...observed]
            const unique = new Map(combined.map((option) => [option.id, option]))
            return {
              key: documentLoadKey,
              query: requestedSearch,
              items: [...unique.values()],
              page,
              hasMore: loadedPage.hasMore,
              loading: false
            }
          })
        })
        .catch(() => {
          if (documentRequest.current !== request) return
          setDocumentLoad((current) => {
            if (
              current?.key !== documentLoadKey ||
              current.query !== requestedSearch ||
              current.page !== (replace ? 0 : page - 1)
            ) {
              return current
            }
            return {
              ...current,
              hasMore: false,
              loading: false,
              error: true
            }
          })
        })
    },
    [documentLoadKey, projectId, services]
  )

  useEffect(() => {
    if (!needsDocuments) return () => undefined
    const request = documentRequest.current + 1
    documentRequest.current = request
    void services
      .loadFactDocuments(projectId, { page: 1, search: '' })
      .then((result) => {
        if (documentRequest.current !== request) return
        const loadedPage = Array.isArray(result) ? { items: result, hasMore: false } : result
        setDocumentLoad({
          key: documentLoadKey,
          query: '',
          items: loadedPage.items.filter(isObservedDocument),
          page: 1,
          hasMore: loadedPage.hasMore,
          loading: false
        })
      })
      .catch(() => {
        if (documentRequest.current !== request) return
        setDocumentLoad({
          key: documentLoadKey,
          query: '',
          items: [],
          page: 0,
          hasMore: false,
          loading: false,
          error: true
        })
      })
    return () => {
      documentRequest.current += 1
    }
  }, [documentLoadKey, needsDocuments, projectId, services])

  if (needsDocuments) {
    const activeLoad = documentLoad?.key === documentLoadKey ? documentLoad : undefined
    const multiple = entry.rawValue.type === 'document-references'
    const saveDocuments = () => {
      const selected = selectedDocuments.map((option) => ({
        docId: option.id,
        fileName: option.label,
        updatedAtTsMs: option.updatedAtTsMs
      }))
      if (selected.length === 0 || selected.length > (multiple ? 32 : 1)) return
      onSave(
        multiple
          ? { type: 'document-references', documents: selected }
          : { type: 'document-reference', document: selected[0]! }
      )
    }
    return (
      <fieldset className="col-span-2 space-y-3 rounded-md border p-3">
        <legend className="px-1 text-xs font-medium">Replace {entry.label}</legend>
        <div className="flex gap-2">
          <Input
            aria-label="Search available documents"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Button type="button" size="sm" variant="outline" onClick={() => loadDocumentPage(1, search.trim(), true)}>
            Search documents
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Selected {selectedDocuments.length} of {multiple ? 32 : 1}
        </p>
        {activeLoad === undefined || activeLoad.loading ? (
          <p className="text-xs text-muted-foreground">Loading available documents</p>
        ) : null}
        {activeLoad?.error && (
          <div className="text-xs text-destructive" role="alert">
            <p>Available documents could not be loaded.</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => setLoadAttempt((v) => v + 1)}
            >
              Try again
            </Button>
          </div>
        )}
        {activeLoad !== undefined && !activeLoad.loading && !activeLoad.error && activeLoad.items.length === 0 && (
          <p className="text-xs text-muted-foreground">No observed documents are available.</p>
        )}
        {activeLoad?.items.map((option) => {
          const checked = selectedDocuments.some((selected) => selected.id === option.id)
          return (
            <label key={option.id} className="flex items-start gap-2 text-sm">
              <input
                type={multiple ? 'checkbox' : 'radio'}
                name={`replacement:${entry.key}`}
                checked={checked}
                disabled={pending || (!checked && selectedDocuments.length >= (multiple ? 32 : 1))}
                onChange={() =>
                  setSelectedDocuments((current) =>
                    multiple
                      ? checked
                        ? current.filter((selected) => selected.id !== option.id)
                        : [...current, option]
                      : [option]
                  )
                }
              />
              <span>{option.label}</span>
            </label>
          )
        })}
        {activeLoad?.hasMore && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={activeLoad.loading}
            onClick={() => loadDocumentPage(activeLoad.page + 1, activeLoad.query, false)}
          >
            Load more documents
          </Button>
        )}
        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={pending || selectedDocuments.length === 0} onClick={saveDocuments}>
            Save {entry.label}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </fieldset>
    )
  }

  const candidates = artifactReplacementCandidates(state, hydratedBrief)
  return (
    <fieldset className="col-span-2 space-y-3 rounded-md border p-3">
      <legend className="px-1 text-xs font-medium">Replace {entry.label}</legend>
      {candidates.map((artifact) => {
        const key = `${artifact.proposalId}:${artifact.sectionId}:${artifact.observation.proposalUpdatedAtTsMs}:${artifact.observation.sectionUpdatedAtTsMs}`
        return (
          <label key={key} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name={`replacement:${entry.key}`}
              checked={selectedArtifactKey === key}
              disabled={pending}
              onChange={() => setSelectedArtifactKey(key)}
            />
            <span>
              Proposal {artifact.proposalId}, section {artifact.sectionId}
            </span>
          </label>
        )
      })}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={pending || selectedArtifactKey === ''}
          onClick={() => {
            const artifact = candidates.find((candidate) => {
              const key = `${candidate.proposalId}:${candidate.sectionId}:${candidate.observation.proposalUpdatedAtTsMs}:${candidate.observation.sectionUpdatedAtTsMs}`
              return key === selectedArtifactKey
            })
            if (artifact !== undefined) onSave({ type: 'artifact-reference', artifact })
          }}
        >
          Save {entry.label}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </fieldset>
  )
}

function FactsPanel({
  projectId,
  briefId,
  scope,
  state,
  services,
  onNavigate,
  onAnswerSlot
}: {
  projectId: number
  briefId: number
  scope: string
  state: ConversationState
  services: ConversationPanelServices
  onNavigate: (identity: ChatInspectorIdentity) => void
  onAnswerSlot?: (stem: string) => void
}) {
  const stateBrief = state.briefs.find((candidate) => candidate.briefId === briefId)
  const [briefOverride, setBriefOverride] = useState<ConversationBrief>()
  const [editing, setEditing] = useState<EditDraft>()
  const [replacing, setReplacing] = useState<string>()
  const [pending, setPending] = useState(false)
  const [notice, setNotice] = useState<string>()
  const [error, setError] = useState<string>()
  const [outputOverrides, setOutputOverrides] = useState<
    Record<number, { baseSignature: string; value: ConversationStaleness }>
  >({})
  const [briefLoadAttempt, setBriefLoadAttempt] = useState(0)
  const invalidated = state.invalidatedBriefIds.includes(briefId)
  const needsHydration = stateBrief === undefined || invalidated
  const briefLoadKey = `${projectId}:${briefId}:${invalidated ? state.highWaterEventId : 'direct'}:${briefLoadAttempt}`
  const [briefLoad, setBriefLoad] = useState<{ key: string; value?: ConversationBrief; error?: true }>()
  const correctionRequest = useRef<{ signature: string; id: string } | undefined>(undefined)
  const executeRequest = useRef<{ signature: string; id: string } | undefined>(undefined)

  useEffect(() => {
    if (!needsHydration) return () => undefined
    let active = true
    void services
      .getBrief(projectId, briefId)
      .then((value) => {
        if (!active) return
        if (value.briefId !== briefId) throw new Error('brief identity mismatch')
        setBriefLoad({ key: briefLoadKey, value })
      })
      .catch(() => {
        if (active) setBriefLoad({ key: briefLoadKey, error: true })
      })
    return () => {
      active = false
    }
  }, [briefId, briefLoadKey, needsHydration, projectId, services])

  const activeBriefLoad = briefLoad?.key === briefLoadKey ? briefLoad : undefined
  const currentBrief = [stateBrief, activeBriefLoad?.value, briefOverride]
    .filter((candidate): candidate is ConversationBrief => candidate !== undefined)
    .sort((left, right) => right.version - left.version)[0]

  if (currentBrief === undefined) {
    if (activeBriefLoad?.error) {
      return (
        <PanelFailure
          message="This brief could not be loaded."
          retry={() => setBriefLoadAttempt((value) => value + 1)}
        />
      )
    }
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading facts</p>
  }
  if (activeBriefLoad?.error && invalidated) {
    return (
      <PanelFailure message="This brief could not be loaded." retry={() => setBriefLoadAttempt((value) => value + 1)} />
    )
  }
  const briefState =
    invalidated && activeBriefLoad?.value === undefined
      ? { refreshRequired: true, whatIHave: [], openSlots: [] }
      : selectFactSpine({ ...state, briefs: [currentBrief] }, briefId)
  if (briefState === undefined) return <PanelFailure message="This brief is unavailable." />

  const submitCorrection = async (entry: FactSpineEntry, clear: boolean, replacement?: ReplacementValue) => {
    if (!clear && editing === undefined && replacement === undefined) return
    const value = clear
      ? undefined
      : (replacement ??
        (editing?.kind === 'integer'
          ? { type: 'integer' as const, integer: Number(editing.value) }
          : { type: 'text' as const, text: editing?.value.trim() ?? '' }))
    if (
      !clear &&
      replacement === undefined &&
      (editing?.value.trim() === '' ||
        (editing?.kind === 'integer' && !Number.isSafeInteger(value && 'integer' in value ? value.integer : NaN)))
    ) {
      setError(`${entry.label} needs a valid value.`)
      return
    }
    const signature = JSON.stringify({ briefId, version: currentBrief.version, key: entry.key, value, clear })
    const clientTurnId = nextRequestID(correctionRequest, signature)
    const correction: ConversationBriefCorrection = {
      clientTurnId,
      expectedVersion: currentBrief.version,
      content: clear ? `Clear ${entry.label}` : `Update ${entry.label}`,
      includeOrgContent: false,
      set: clear || value === undefined ? [] : [{ key: entry.key, value }],
      clear: clear ? [entry.key] : []
    }
    setPending(true)
    setError(undefined)
    setNotice(undefined)
    try {
      const updated = await services.correctBrief(projectId, briefId, correction)
      if (
        updated.briefId !== briefId ||
        updated.version !== currentBrief.version + 1 ||
        updated.taskClass !== currentBrief.taskClass ||
        updated.definitionVersion !== currentBrief.definitionVersion
      ) {
        throw new Error('The corrected brief authority did not match this edit.')
      }
      setBriefOverride(updated)
      setEditing(undefined)
      setReplacing(undefined)
      const staleStatuses = await Promise.allSettled(
        state.outputs
          .filter((output) => output.briefId === briefId)
          .map((output) => services.getOutputStaleness(projectId, output.messageId))
      )
      const refreshed = staleStatuses.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
      if (refreshed.length > 0) {
        setOutputOverrides((current) => ({
          ...current,
          ...Object.fromEntries(
            refreshed.map((output) => {
              const base = state.outputs.find((candidate) => candidate.messageId === output.messageId)
              return [
                output.messageId,
                { baseSignature: base === undefined ? '' : outputAuthoritySignature(base), value: output }
              ]
            })
          )
        }))
      }
      setNotice(
        staleStatuses.some((result) => result.status === 'rejected')
          ? `${entry.label} updated. Output status will refresh with the conversation.`
          : `${entry.label} updated.`
      )
    } catch (correctionError) {
      if (isVersionConflict(correctionError)) {
        try {
          const latest = await services.getBrief(projectId, briefId)
          if (
            latest.briefId !== briefId ||
            latest.version <= currentBrief.version ||
            latest.taskClass !== currentBrief.taskClass ||
            latest.definitionVersion !== currentBrief.definitionVersion
          ) {
            throw new Error('The current brief identity did not match the selected brief.')
          }
          setBriefOverride(latest)
          correctionRequest.current = undefined
          setError(`Brief changed while you were editing. Review your draft against version ${latest.version}.`)
        } catch (reloadError) {
          setError(failureLine(reloadError, loadFailureFallback))
        }
      } else {
        setError(failureLine(correctionError, loadFailureFallback))
      }
    } finally {
      setPending(false)
    }
  }

  const continueBrief = async () => {
    const signature = `${briefId}:${currentBrief.version}`
    const clientRequestId = nextRequestID(executeRequest, signature)
    setPending(true)
    setError(undefined)
    try {
      await services.executeBrief(projectId, briefId, { clientRequestId, expectedVersion: currentBrief.version })
      setNotice('Work queued from this brief.')
    } catch (executionError) {
      setError(failureLine(executionError, loadFailureFallback))
    } finally {
      setPending(false)
    }
  }

  const questionBudgetExhausted = state.tasks.some(
    (task) =>
      task.briefId === briefId &&
      task.questionStatus === 'succeeded' &&
      task.questionStage === 'brief.questions.budget_exhausted' &&
      (task.executionId === null || task.executionId === undefined)
  )
  const relatedOutputs = state.outputs
    .filter((output) => output.briefId === briefId)
    .map((output) => {
      const override = outputOverrides[output.messageId]
      return override?.baseSignature === outputAuthoritySignature(output) ? override.value : output
    })

  return (
    <div>
      <PanelHeader title="Facts" scope={scope} />
      <div className="px-5 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">What I have</h3>
          <span className="text-xs text-muted-foreground">Version {currentBrief.version}</span>
        </div>
        {briefState.refreshRequired ? (
          <p className="text-sm text-muted-foreground">Refreshing the current facts.</p>
        ) : (
          <dl className="divide-y">
            {briefState.whatIHave.map((entry) => {
              const isEditing = editing?.key === entry.key
              const isReplacing = replacing === entry.key
              const replaceable =
                entry.rawValue.type === 'document-reference' ||
                entry.rawValue.type === 'document-references' ||
                entry.rawValue.type === 'artifact-reference'
              return (
                <div key={entry.key} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 py-3">
                  <div className="min-w-0">
                    <dt className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      {entry.label}
                      {entry.required && <span className="font-normal">Required</span>}
                    </dt>
                    <dd className="mt-1 text-sm leading-6">
                      {isEditing ? (
                        <Input
                          type={editing.kind === 'integer' ? 'number' : 'text'}
                          aria-label={entry.label}
                          value={editing.value}
                          onChange={(event) => setEditing({ ...editing, value: event.target.value })}
                          disabled={pending}
                        />
                      ) : (
                        entry.value
                      )}
                    </dd>
                    <FactProvenance entry={entry} onNavigate={onNavigate} />
                    <FactSourceDisclosure entry={entry} services={services} />
                  </div>
                  <div className="flex items-start gap-1">
                    {isEditing ? (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void submitCorrection(entry, false)}
                          disabled={pending}
                        >
                          Save {entry.label}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(undefined)}
                          disabled={pending}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        {(entry.kind === 'text' || entry.kind === 'integer' || replaceable) && (
                          <Button
                            type="button"
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Change ${entry.label}`}
                            onClick={() => {
                              if (replaceable) {
                                setEditing(undefined)
                                setReplacing(entry.key)
                                return
                              }
                              setReplacing(undefined)
                              setEditing({
                                key: entry.key,
                                label: entry.label,
                                kind: entry.rawValue.type === 'integer' ? 'integer' : 'text',
                                value:
                                  entry.rawValue.type === 'integer'
                                    ? String(entry.rawValue.integer)
                                    : entry.rawValue.type === 'text'
                                      ? entry.rawValue.text
                                      : ''
                              })
                            }}
                          >
                            <Pencil className="size-3.5" aria-hidden="true" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Clear ${entry.label}`}
                          onClick={() => void submitCorrection(entry, true)}
                          disabled={pending}
                        >
                          <X className="size-3.5" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </div>
                  {isReplacing && replaceable && (
                    <FactReplacementEditor
                      key={`${entry.key}:${currentBrief.version}`}
                      projectId={projectId}
                      entry={entry}
                      hydratedBrief={currentBrief}
                      state={state}
                      services={services}
                      pending={pending}
                      onCancel={() => setReplacing(undefined)}
                      onSave={(value) => void submitCorrection(entry, false, value)}
                    />
                  )}
                </div>
              )
            })}
          </dl>
        )}
      </div>
      {briefState.openSlots.length > 0 && (
        <section className="border-t px-5 py-4">
          <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">Open</h3>
          {/*
            This listed raw registry keys, so a reader saw `source-material` and had
            nothing to press. It now carries the label, and where the slot is answered by
            typing, an arrow that hands the composer the opening of the sentence. A slot
            answered by choosing from a list gets no arrow rather than a disabled one:
            there is no sentence to seed, and the question itself offers the list.
          */}
          <ul className="mt-3 space-y-2 text-sm">
            {briefState.openSlots.map((slot) => (
              <li key={slot.key} className="flex items-center justify-between gap-2">
                <span className="italic">{slot.label}</span>
                {slot.answerStem !== '' && onAnswerSlot !== undefined && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Answer ${slot.label}`}
                    onClick={() => onAnswerSlot(slot.answerStem)}
                  >
                    <ArrowRight className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {relatedOutputs.map((output) => (
        <div key={output.messageId}>
          <OutputDecision projectId={projectId} output={output} services={services} />
          <ArtifactActions projectId={projectId} state={state} output={output} services={services} />
        </div>
      ))}
      {questionBudgetExhausted && currentBrief.complete && (
        <div className="border-t px-5 py-4">
          <p className="mb-3 text-sm text-muted-foreground">
            The question budget is complete. This brief is ready to continue.
          </p>
          <Button type="button" size="sm" onClick={() => void continueBrief()} disabled={pending}>
            Continue with this brief
          </Button>
        </div>
      )}
      {(notice !== undefined || error !== undefined) && (
        <p
          className={cn('px-5 py-3 text-sm', error === undefined ? 'text-muted-foreground' : 'text-destructive')}
          role="status"
        >
          {error ?? notice}
        </p>
      )}
    </div>
  )
}

function SourcesPanel({
  projectId,
  messageId,
  scope,
  state,
  services
}: {
  projectId: number
  messageId: number
  scope: string
  state: ConversationState
  services: ConversationPanelServices
}) {
  const message = [...state.olderMessages, ...state.liveMessages].find((candidate) => candidate.messageId === messageId)
  const computed = message?.author === 'computed-facts' ? (message.computedResults ?? []) : []
  const citations = message === undefined ? [] : safeCitationIDs(message.refs)
  const evidence = message === undefined ? [] : safeMessageEvidence(message.refs, message.content, citations)
  const sourceIDs = [...new Set([...citations, ...computed.flatMap((result) => result.sourceDocumentIds)])]
  const details = useSourceDetails(sourceIDs, services.getSourceSummaries)
  const loadDetails = details.load
  useEffect(() => loadDetails(), [loadDetails])
  const summariesByID = new Map(details.summaries?.map((summary) => [summary.docId, summary]) ?? [])
  const evidenceGroups = new Map<string, { claim: string; links: Array<SafeMessageEvidence> }>()
  for (const link of evidence) {
    const key = `${link.claimStartByte}:${link.claimEndByte}`
    const group = evidenceGroups.get(key) ?? { claim: link.claim, links: [] }
    group.links.push(link)
    evidenceGroups.set(key, group)
  }
  const visibleEvidenceGroups = [...evidenceGroups.values()]
    .map((group) => ({
      ...group,
      links: group.links.filter((link) => summariesByID.has(link.documentId))
    }))
    .filter((group) => group.links.length > 0)
  if (message === undefined) {
    return (
      <div>
        <PanelHeader title="Sources" scope={scope} />
        <PanelFailure message="This message is not in the retained conversation window." />
      </div>
    )
  }
  const output = state.outputs.find((candidate) => candidate.messageId === messageId)
  return (
    <div>
      <PanelHeader title="Sources" scope={scope} />
      <div className="space-y-4 px-5 py-4">
        {details.errorMessage !== null && (
          <div className="text-sm text-destructive">
            <p role="alert">{details.errorMessage}</p>
            {details.retryable && (
              <Button type="button" size="sm" variant="outline" className="mt-2" onClick={details.retry}>
                Retry source details
              </Button>
            )}
          </div>
        )}
        {(details.loading || details.summaries === null) && details.errorMessage === null && (
          <p className="text-sm text-muted-foreground" role="status">
            Loading source details
          </p>
        )}
        {details.summaries !== null && visibleEvidenceGroups.length > 0 && (
          <section aria-label="Claim evidence" className="space-y-4">
            {visibleEvidenceGroups.map((group) => {
              const claimStartByte = group.links[0]?.claimStartByte
              const claimEndByte = group.links[0]?.claimEndByte
              const claimLabelID = `claim-evidence-${messageId}-${claimStartByte}-${claimEndByte}`
              return (
                <div
                  key={`${claimStartByte}:${claimEndByte}`}
                  role="group"
                  aria-labelledby={claimLabelID}
                  className="space-y-2"
                >
                  <div id={claimLabelID} className="text-sm leading-relaxed font-medium text-foreground">
                    <MarkdownRenderer className="[&_ol]:mb-0 [&_p]:mb-0 [&_ul]:mb-0">{group.claim}</MarkdownRenderer>
                  </div>
                  <div className="space-y-2">
                    {group.links.map((link) => {
                      const summary = summariesByID.get(link.documentId)
                      if (summary === undefined) return null
                      return (
                        <div key={link.documentId} className="rounded-md bg-muted/35 px-3 py-2.5">
                          <p className="text-xs font-medium text-foreground">
                            {summary.fileName.trim() || 'Workspace source'}
                          </p>
                          <blockquote className="mt-1 border-l-2 border-border pl-2.5 text-xs leading-relaxed text-muted-foreground">
                            {link.excerpt}
                          </blockquote>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </section>
        )}
        {details.summaries !== null && citations.length > 0 && (
          <div
            role="list"
            {...(visibleEvidenceGroups.length === 0 ? {} : { 'aria-label': 'All sources' })}
            className={cn('divide-y', visibleEvidenceGroups.length > 0 && 'border-t pt-2')}
          >
            {citations.map((documentID, index) => {
              const summary = summariesByID.get(documentID)
              if (summary === undefined) return null
              return (
                <div key={documentID} className="flex gap-3 py-1">
                  <span className="mt-1.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {index + 1}
                  </span>
                  <SourceSummaryRow summary={summary} />
                </div>
              )
            })}
          </div>
        )}
        {computed.map((result, index) => (
          <div
            key={`${messageId}:${index}`}
            role="group"
            aria-label={`Computed result ${index + 1} sources`}
            className="border-b pb-3 text-sm last:border-0"
          >
            {result.table === null || result.table === undefined ? (
              <ComputedResultFallback markdown={result.markdown ?? ''} />
            ) : (
              <>
                <p className="font-medium">{result.table.title}</p>
                <p className="mt-1 flex justify-between text-xs text-muted-foreground">
                  <span>Cells computed</span>
                  <span className="tabular-nums">{result.table.cellsComputed}</span>
                </p>
                {/* The values themselves, not just a count. ADR-173 declines to
                    make a computed result an artifact on the grounds that this
                    panel already lists a message's computed facts, so this panel
                    has to keep listing them. */}
                <div className="mt-2 rounded-md border">
                  <ComputedResultTable table={result.table} />
                </div>
              </>
            )}
            {details.summaries !== null && result.sourceDocumentIds.length > 0 && (
              <div role="list" className="mt-2 divide-y border-t pt-1">
                {[...new Set(result.sourceDocumentIds)].map((documentID) => {
                  const summary = summariesByID.get(documentID)
                  return summary === undefined ? null : <SourceSummaryRow key={documentID} summary={summary} />
                })}
              </div>
            )}
          </div>
        ))}
        {citations.length === 0 && computed.length === 0 && (
          <p className="text-sm text-muted-foreground">No durable source references are attached to this message.</p>
        )}
      </div>
      {output !== undefined && <OutputDecision projectId={projectId} output={output} services={services} />}
    </div>
  )
}

function activityEntryLabel(entry: ReturnType<typeof selectConversationTurnPresentations>[number]['activity'][number]) {
  switch (entry.kind) {
    case 'change': {
      const size =
        entry.change.kind === 'artifact' ? changeSizeLabel(entry.change.words, entry.change.previousWords) : undefined
      return [entry.change.kind === 'artifact' ? entry.change.targetName : undefined, entry.change.label, size]
        .filter(Boolean)
        .join(' · ')
    }
    case 'stopped':
      return entry.reason === 'cancelled' ? 'Work cancelled' : 'Work stopped before it finished'
    case 'intake-stopped':
      return entry.reason === 'cancelled' ? 'Task cancelled' : 'Could not start this task'
    case 'exhausted':
      return 'Stopped asking, some details are still open'
    case 'message':
    case 'attempt':
    case 'question':
      return ''
  }
}

function ActivityPanel({
  originMessageId,
  scope,
  state
}: {
  originMessageId: number
  scope: string
  state: ConversationState
}) {
  const turn = selectConversationTurnPresentations(state).find(
    (candidate) => candidate.originMessage?.messageId === originMessageId
  )
  if (turn === undefined) {
    return (
      <div>
        <PanelHeader title="Activity" scope={scope} />
        <PanelFailure message="This request is not in the retained conversation window." />
      </div>
    )
  }
  const steps = turn.executionSteps.map(stageLabel).filter((label) => label !== '')
  const activity = turn.activity.map(activityEntryLabel).filter((label) => label !== '')
  return (
    <div>
      <PanelHeader title="Activity" scope={scope} />
      <div className="px-5 py-4">
        {steps.length === 0 && activity.length === 0 ? (
          <p className="text-sm text-muted-foreground">No additional activity was recorded for this request.</p>
        ) : (
          <ol className="space-y-3 text-sm">
            {[...steps, ...activity].map((label, index) => (
              <li key={`${index}:${label}`} className="flex gap-2.5">
                <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span>{label}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

function ArtifactActions({
  projectId,
  state,
  output,
  services
}: {
  projectId: number
  state: ConversationState
  output?: ConversationStaleness
  services: ConversationPanelServices
}) {
  const latestMatchingTask = (predicate: (candidate: ConversationState['tasks'][number]) => boolean) => {
    let match: ConversationState['tasks'][number] | undefined
    for (const candidate of state.tasks) {
      if (predicate(candidate)) match = candidate
    }
    return match
  }
  const task =
    output === undefined
      ? undefined
      : (latestMatchingTask(
          (candidate) =>
            candidate.executionId !== null &&
            candidate.executionMode === 'redraft' &&
            candidate.executionSourceMessageId === output.messageId
        ) ??
        latestMatchingTask(
          (candidate) =>
            candidate.executionId !== null && candidate.executionOutputMessageIds.includes(output.messageId)
        ))
  const projectedStatus =
    task?.executionId === null || task?.executionId === undefined
      ? task?.executionStatus
      : (state.executionStates[task.executionId]?.status ?? task.executionStatus)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()
  const [localStatus, setLocalStatus] = useState<{
    executionId: number
    sourceStatus: ConversationState['tasks'][number]['executionStatus']
    status: ConversationRun['status']
  }>()
  const action = async (operation: () => Promise<ConversationRun>) => {
    setPending(true)
    setError(undefined)
    try {
      const result = await operation()
      if (task?.executionId !== null && task?.executionId !== undefined) {
        setLocalStatus({ executionId: task.executionId, sourceStatus: projectedStatus, status: result.status })
      }
    } catch (actionError) {
      setError(failureLine(actionError, loadFailureFallback))
    } finally {
      setPending(false)
    }
  }
  if (task?.executionId === null || task?.executionId === undefined) return null
  const executionStatus =
    localStatus?.executionId === task.executionId && localStatus.sourceStatus === projectedStatus
      ? localStatus.status
      : projectedStatus
  const canCancel =
    executionStatus === 'queued' || executionStatus === 'running' || executionStatus === 'waiting_for_input'
  const canRetry = executionStatus === 'failed'
  if (!canCancel && !canRetry) return null
  return (
    <div className="border-t px-5 py-4">
      <div className="flex gap-2">
        {canCancel && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => void action(() => services.cancelExecution(projectId, task.executionId!))}
          >
            Cancel work
          </Button>
        )}
        {canRetry && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => void action(() => services.retryExecution(projectId, task.executionId!))}
          >
            Retry work
          </Button>
        )}
      </div>
      {error !== undefined && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  )
}

function scrollArtifactAnchorIntoView(element: HTMLElement | null) {
  if (element === null || typeof element.scrollIntoView !== 'function') return
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' })
}

// Each canvas calls useCanvasExport above its own loading and failure returns for the
// reason DocumentPanel gives for choosing its revision there: a hook cannot be called after
// a return, and the control it drives belongs to a header that sits below both of them.
// These stand in for the artifact until the read lands. They never reach the screen, because
// every canvas returns its loading line above the header the control sits in, and their
// identity is constant so the controller's own reset does not fire on every render.
const unloadedProposal: Proposal = {
  proposalId: 0,
  projectId: 0,
  documentName: '',
  clientName: '',
  status: 'unknown',
  createdOnTsMs: 0,
  updatedAtTsMs: 0,
  sections: []
}

const unloadedFitLayout = {
  titleCharsPerLine: 1,
  maxTitleLines: 1,
  bodyCharsPerLine: 1,
  maxBodyLines: 1,
  maxKeyMessageLines: 1,
  maxPointLines: 1,
  maxPoints: 1
}

const unloadedPresentation: Presentation = {
  presentationId: 0,
  projectId: 0,
  title: '',
  purpose: '',
  audience: '',
  targetSlideCount: 0,
  themeDocId: null,
  status: 'drafting',
  createdOnTsMs: 0,
  updatedAtTsMs: 0,
  slides: [],
  sourceArtifacts: [],
  fitPolicy: {
    version: 1,
    comfortablePercent: 100,
    layouts: {
      title_and_content: unloadedFitLayout,
      two_column: unloadedFitLayout,
      section_header: unloadedFitLayout
    }
  }
}

const unloadedRevision: DocumentRevision = {
  versionId: 0,
  ordinal: 0,
  title: '',
  content: '',
  sourceDocIds: [],
  createdBy: 0,
  createdOnTsMs: 0
}

// One latest-run read per mounted artifact panel, feeding BuildRunStrip its initial run.
// The read is a small effect beside the artifact load rather than part of it: the strip's
// state is per artifact and the component is keyed by that identity below, so a switch of
// artifact remounts it instead of carrying one deck's run into another. A failed or
// superseded read leaves the strip unmounted - there is no run to show - while the strip
// itself owns surfacing its own poll and operation failures.
function ArtifactBuildRunStrip({
  kind,
  projectId,
  artifactId,
  services,
  onSettled,
  refreshToken = 0,
  onActivityChange,
  onChooseSources
}: {
  kind: 'proposal' | 'presentation'
  projectId: number
  artifactId: number
  services: MergedPanelServices
  onSettled: () => void
  refreshToken?: number
  onActivityChange?: (active: boolean) => void
  onChooseSources?: () => void
}): ReactNode {
  const [initialRun, setInitialRun] = useState<AgentRun | null>(null)
  // Declared before the read below so the freshest service object lands in the ref first.
  const servicesRef = useRef(services)
  useEffect(() => {
    servicesRef.current = services
  })
  useEffect(() => {
    let active = true
    const read =
      kind === 'proposal'
        ? servicesRef.current.getLatestProposalBuildRun(projectId, artifactId)
        : servicesRef.current.getLatestPresentationBuildRun(projectId, artifactId)
    void read
      .then((run) => {
        if (active) setInitialRun(run)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [artifactId, kind, projectId, refreshToken])
  // Stable across renders while the ids hold: useBuildRun resets itself whenever it sees a
  // different getRun, so a fresh closure per render would restart polling forever.
  const controls = useMemo<BuildRunStripControls>(
    () =>
      kind === 'proposal'
        ? {
            getRun: (runId, signal) => servicesRef.current.getProposalBuildRun(projectId, artifactId, runId, signal),
            startRun: () => servicesRef.current.startProposalBuildRun(projectId, artifactId, { modelTemp: 0.5 }),
            cancelRun: (runId) => servicesRef.current.cancelProposalBuildRun(projectId, artifactId, runId),
            retryRun: (runId) => servicesRef.current.retryProposalBuildRun(projectId, artifactId, runId)
          }
        : {
            getRun: (runId, signal) =>
              servicesRef.current.getPresentationBuildRun(projectId, artifactId, runId, signal),
            startRun: () => servicesRef.current.startPresentationBuildRun(projectId, artifactId),
            cancelRun: (runId) => servicesRef.current.cancelPresentationBuildRun(projectId, artifactId, runId),
            retryRun: (runId) => servicesRef.current.retryPresentationBuildRun(projectId, artifactId, runId)
          },
    [artifactId, kind, projectId]
  )
  return (
    <BuildRunStrip
      initialRun={initialRun}
      controls={controls}
      {...(onChooseSources === undefined ? {} : { onChooseSources })}
      {...(onActivityChange === undefined ? {} : { onActivityChange })}
      onSettled={() => {
        onSettled()
      }}
      // A single progress surface owns active status and cancellation. At terminal it yields
      // to the settled status and recovery actions.
      progressDetail={(run, buildRun) =>
        kind === 'proposal' ? (
          <ProposalBuildProgress
            compact
            run={run}
            failureMessage={buildRun.failureMessage}
            stopping={buildRun.isCancelling}
            retrying={buildRun.isRetrying}
            retryDisabled={false}
            onStop={() => void buildRun.cancel()}
            onRetry={() => void buildRun.retry()}
          />
        ) : (
          <PresentationBuildProgress
            run={run}
            starting={buildRun.isStarting}
            stopping={buildRun.isCancelling}
            retrying={buildRun.isRetrying}
            retryDisabled={false}
            onStop={() => void buildRun.cancel()}
            onRetry={() => void buildRun.retry()}
          />
        )
      }
    />
  )
}

// The deck-level disclosure beside the presentation metadata block. Expanding reads the
// whole-deck message history once; submitting posts one refine and hands the reload to the
// panel, then rereads the history so what is on screen is what the server kept.
function DeckRefineDisclosure({
  projectId,
  presentationId,
  services,
  onChanged
}: {
  projectId: number
  presentationId: number
  services: MergedPanelServices
  onChanged: () => void
}): ReactNode {
  const [expanded, setExpanded] = useState(false)
  const [messages, setMessages] = useState<Array<PresentationMessage>>([])
  const [loading, setLoading] = useState(false)
  const [readError, setReadError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  const toggle = () => {
    if (expanded) {
      setExpanded(false)
      return
    }
    setExpanded(true)
    setLoading(true)
    setReadError(undefined)
    void services
      .getPresentationMessages(projectId, presentationId)
      .then((value) => setMessages(value))
      .catch((readFailure) => {
        setMessages([])
        setReadError(failureLine(readFailure, loadFailureFallback))
      })
      .finally(() => setLoading(false))
  }

  const submit = (instruction: string) => {
    if (busy) return
    setBusy(true)
    setError(undefined)
    void (async () => {
      try {
        await services.refinePresentationDeck(projectId, presentationId, instruction)
      } catch (refineFailure) {
        setError(failureLine(refineFailure, loadFailureFallback))
        return
      } finally {
        setBusy(false)
      }
      onChanged()
      // The deck itself already reloaded through onChanged(); a failed history reread keeps
      // the previous list rather than reporting a refine failure that did not happen.
      services
        .getPresentationMessages(projectId, presentationId)
        .then((value) => setMessages(value))
        .catch(() => undefined)
    })()
  }

  return (
    <div className="mt-4 border-t pt-3">
      <button
        type="button"
        className="text-xs font-medium text-foreground hover:underline"
        aria-expanded={expanded}
        onClick={toggle}
      >
        Refine deck
      </button>
      {expanded && (
        <div className="mt-2 space-y-3">
          {loading && (
            <p role="status" className="text-xs text-muted-foreground">
              Loading deck history...
            </p>
          )}
          {!loading && readError !== undefined && (
            <p role="alert" className="text-xs text-destructive">
              {readError}
            </p>
          )}
          {!loading && readError === undefined && messages.length === 0 && (
            <p className="text-xs text-muted-foreground">No refinements yet.</p>
          )}
          {messages.length > 0 && (
            <ul className="space-y-2 rounded-md bg-muted/25 px-3 py-2">
              {messages.map((message) => (
                <li key={message.messageId} className="text-sm">
                  <span className="font-medium">{message.author === 'user' ? 'You' : 'Snowvault'}</span>
                  <span className="mt-0.5 block break-words whitespace-pre-wrap text-muted-foreground">
                    {message.content}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <RefineComposer
            placeholder="Describe how the whole deck should change"
            label="Refining the whole deck"
            busy={busy}
            {...(error === undefined ? { error: null } : { error })}
            onSubmit={submit}
          />
        </div>
      )}
    </div>
  )
}

function proposalSectionStateLabel(section: ProposalSection): string {
  if (section.state === 'pending') return section.kind === 'rewrite' ? 'Awaiting first draft' : 'Not written'
  return section.state.charAt(0).toUpperCase() + section.state.slice(1)
}

function TemplateGuidance({ markdown }: { markdown: string }): ReactNode {
  return (
    <div className="rounded-md border border-dashed bg-muted/25 px-3 py-3">
      <p className="text-xs font-medium text-foreground">Template guidance</p>
      {markdown.trim() === '' ? (
        <p className="mt-1.5 text-sm text-muted-foreground italic">No template guidance was provided.</p>
      ) : (
        <div className="mt-1.5 text-sm leading-6 text-muted-foreground">
          <MarkdownRenderer>{markdown}</MarkdownRenderer>
        </div>
      )}
    </div>
  )
}

function ProposalDraftingResumeButton({
  buildActive,
  busy,
  disabled = false,
  onResume
}: {
  buildActive: boolean
  busy: boolean
  disabled?: boolean
  onResume: () => void
}): ReactNode {
  const accessibleName = buildActive
    ? 'Proposal drafting is in progress'
    : 'Resume proposal drafting for all pending sections'
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label={accessibleName}
      disabled={buildActive || busy || disabled}
      onClick={onResume}
    >
      {(buildActive || busy) && (
        <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      )}
      {buildActive ? 'Drafting...' : busy ? 'Resuming...' : 'Resume drafting'}
    </Button>
  )
}

function PendingManualSectionAction({
  section,
  onWrite
}: {
  section: ProposalSection
  onWrite: () => void
}): ReactNode {
  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">Add your own content to complete this section.</p>
        <Button type="button" size="sm" variant="outline" onClick={onWrite}>
          <Pencil className="size-3.5" aria-hidden="true" />
          Write {section.heading}
        </Button>
      </div>
    </div>
  )
}

function ProposalPanel({
  projectId,
  proposalId,
  sectionId,
  scope,
  provenance,
  services,
  onClose
}: {
  projectId: number
  proposalId: number
  sectionId?: number
  scope: string
  provenance?: CanvasProvenance
  services: ConversationPanelServices
  // The canvas's own close, the same one PanelHeader's sibling controls use to clear the
  // canvas search params. Delete proposal is the only in-panel action that needs it.
  onClose?: () => void
}) {
  // One merge for the optional mutation surface: a host that has not bound a member yet
  // falls back to the real API function by default rather than meeting a refusal.
  const svc = mergePanelServices(services)
  const revision = useArtifactRevision(projectId, (event) => {
    if (event.kind !== 'proposal' || event.proposalId !== proposalId) return false
    // With no section named the whole proposal is on screen, so any change to it is a
    // reason to reload: a created proposal, or a section somebody else drafted.
    if (sectionId === undefined) return true
    return event.sectionId === sectionId
  })
  // Everything the loaded proposal is identified by, revision mark included, so a matching
  // change event rereads it. The retain key is the same string without the mark: the mark
  // says when to reread, and the key says which artifact a held copy belongs to.
  const identity = `${projectId}:${proposalId}:${sectionId ?? 'whole'}:${revision}`
  const retainKey = `${projectId}:${proposalId}:${sectionId ?? 'whole'}`
  const focusedSectionRef = useRef<HTMLElement | null>(null)
  const {
    artifact: proposal,
    artifactAttempt,
    artifactVersion,
    failed,
    reload
  } = usePanelArtifact<Proposal>({
    identity,
    retainKey,
    enabled: true,
    load: async () => {
      const value = await services.getProposal(projectId, proposalId)
      if (value.projectId !== projectId || value.proposalId !== proposalId) {
        throw new Error('The proposal identity did not match this workspace.')
      }
      return value
    }
  })
  useEffect(() => {
    if (proposal === null) return
    scrollArtifactAnchorIntoView(focusedSectionRef.current)
  }, [proposal, sectionId])
  const { publish } = useCanvasOutline()
  useEffect(() => {
    if (proposal === null) return undefined
    const ordered = [...proposal.sections].sort(
      (left, right) => left.ordinal - right.ordinal || left.sectionId - right.sectionId
    )
    publish({
      kind: 'proposal',
      id: proposalId,
      entries: ordered.map((section, position) => ({
        ordinal: position + 1,
        title: section.heading,
        state: section.state,
        position: { section: section.sectionId }
      })),
      ...(ordered.findIndex((section) => section.sectionId === sectionId) < 0
        ? {}
        : { activeOrdinal: ordered.findIndex((section) => section.sectionId === sectionId) + 1 })
    })
    return () => publish(undefined)
  }, [proposal, proposalId, publish, sectionId])
  // Export is about the proposal rather than about a chat, so it is offered whether or not
  // one is open. A successful export flips the proposal to exported server-side, so the
  // canvas reads it again the way the retry control does.
  const exportController = useCanvasExport({
    projectId,
    target: { kind: 'proposal', proposalId, proposal: proposal ?? unloadedProposal },
    services,
    onExported: reload
  })
  // One shared stream for every section's refine and regenerate; a settled stream is a
  // reason to reread the proposal it just rewrote.
  const refine = useSectionRefine({ projectId, proposalId, onSettled: () => reload() })
  const [titleEditing, setTitleEditing] = useState(false)
  // The section whose heading sits in edit mode after Add section created it, keyed by
  // section id. ProposalSectionActions owns its own rename editing for every other case.
  const [renamingSectionId, setRenamingSectionId] = useState<number>()
  const [bodyEdit, setBodyEdit] = useState<{
    sectionId: number
    mark: Proposal
    saving: boolean
    error?: string
  }>()
  const [savedBody, setSavedBody] = useState<{
    sectionId: number
    requestedReloadAttempt: number
    acknowledgedArtifactVersion?: number
  }>()
  const [restoreBodyEditFocus, setRestoreBodyEditFocus] = useState<number>()
  const bodyEditButtonRefs = useRef(new Map<number, HTMLButtonElement>())
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [proposalBuildActive, setProposalBuildActive] = useState(false)
  const [proposalBuildRefresh, setProposalBuildRefresh] = useState(0)
  const [resumeAttempt, setResumeAttempt] = useState<{ pending: boolean; failure?: string }>()
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [sourceCount, setSourceCount] = useState<number | null>(null)
  const sourcesButtonRef = useRef<HTMLButtonElement>(null)
  const resumeInFlightRef = useRef(false)
  const templateLabel = useSourceDocumentLabel({
    docId: proposal?.templateDocId,
    absentLabel: 'No template recorded',
    load: services.getSourceSummaries
  })
  useEffect(() => {
    if (bodyEdit !== undefined || restoreBodyEditFocus === undefined) return
    bodyEditButtonRefs.current.get(restoreBodyEditFocus)?.focus()
  }, [bodyEdit, restoreBodyEditFocus])
  if (proposal === null) {
    if (failed) return <PanelFailure message="This proposal could not be loaded." retry={reload} />
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading proposal</p>
  }
  if (bodyEdit?.error !== undefined && !bodyEdit.saving && bodyEdit.mark !== proposal) {
    setBodyEdit({ sectionId: bodyEdit.sectionId, mark: proposal, saving: false })
  }
  if (
    savedBody !== undefined &&
    savedBody.acknowledgedArtifactVersion === undefined &&
    artifactAttempt === savedBody.requestedReloadAttempt
  ) {
    setSavedBody({ ...savedBody, acknowledgedArtifactVersion: artifactVersion })
  } else if (
    savedBody?.acknowledgedArtifactVersion !== undefined &&
    savedBody.acknowledgedArtifactVersion !== artifactVersion
  ) {
    setSavedBody(undefined)
  }
  const sections = [...proposal.sections].sort(
    (left, right) => left.ordinal - right.ordinal || left.sectionId - right.sectionId
  )
  const section = sectionId === undefined ? undefined : sections.find((candidate) => candidate.sectionId === sectionId)
  if (sectionId !== undefined && section === undefined) {
    return <PanelFailure message="This section is not part of the selected proposal." />
  }
  const moveSection = async (moving: ProposalSection, direction: 'up' | 'down') => {
    const ids = sections.map((candidate) => candidate.sectionId)
    const at = ids.indexOf(moving.sectionId)
    const swapWith = direction === 'up' ? at - 1 : at + 1
    if (at < 0 || swapWith < 0 || swapWith >= ids.length) return
    const displaced = ids[swapWith]!
    ids[swapWith] = ids[at]!
    ids[at] = displaced
    await svc.reorderSections(projectId, proposalId, ids)
    reload()
  }
  const startBodyEditing = (candidate: ProposalSection) => {
    setTitleEditing(false)
    setRenamingSectionId(undefined)
    setSavedBody(undefined)
    setBodyEdit({ sectionId: candidate.sectionId, mark: proposal, saving: false })
  }
  const pendingRewriteSectionCount = sections.filter(
    (candidate) => candidate.state === 'pending' && candidate.kind === 'rewrite'
  ).length
  const resumeProposalDrafting = () => {
    if (resumeInFlightRef.current || proposalBuildActive || sourcesOpen) return
    resumeInFlightRef.current = true
    setResumeAttempt({ pending: true })
    void (async () => {
      try {
        const latest = await svc.getLatestProposalBuildRun(projectId, proposalId)
        let run = latest
        if (
          latest === null ||
          proposalBuildNeedsFreshRun(latest) ||
          (!isActiveBuildRun(latest) && latest.status !== 'failed')
        ) {
          run = await svc.startProposalBuildRun(projectId, proposalId, { modelTemp: 0.5 })
        } else if (latest.status === 'failed') {
          run = await svc.retryProposalBuildRun(projectId, proposalId, latest.runId)
        }
        setProposalBuildActive(run !== null && isActiveBuildRun(run))
        setProposalBuildRefresh((current) => current + 1)
        setResumeAttempt(undefined)
      } catch (error) {
        setProposalBuildActive(false)
        setResumeAttempt({
          pending: false,
          failure: failureLine(error, 'Proposal drafting could not be resumed.')
        })
      } finally {
        resumeInFlightRef.current = false
      }
    })()
  }
  return (
    <div>
      <PanelHeader
        title={proposal.documentName}
        titleContent={
          bodyEdit === undefined ? (
            <InlineEditableText
              value={proposal.documentName}
              editing={titleEditing}
              onEditingChange={setTitleEditing}
              textClassName="text-sm font-semibold tracking-tight"
              ariaLabel="Rename proposal"
              onSave={async (next) => {
                await svc.renameProposal(projectId, proposalId, next)
                reload()
              }}
              renderDisplay={(start) => (
                <h2 className="truncate text-sm font-semibold tracking-tight">
                  <button
                    type="button"
                    onClick={start}
                    className="block max-w-full truncate rounded-xs text-left hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    {proposal.documentName}
                  </button>
                </h2>
              )}
            />
          ) : (
            <h2 className="truncate text-sm font-semibold tracking-tight">{proposal.documentName}</h2>
          )
        }
        icon={<FileText className="size-4 text-primary" aria-hidden="true" />}
        scope={scope}
        {...(provenance === undefined ? {} : { provenance })}
        details={
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              {proposal.clientName} · {sections.length} {sections.length === 1 ? 'section' : 'sections'} ·{' '}
              {proposal.status.replaceAll('_', ' ')}
            </p>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3">
              <dt className="text-muted-foreground">Template</dt>
              <dd className="truncate" title={templateLabel}>
                {templateLabel}
              </dd>
            </dl>
          </div>
        }
        {...(bodyEdit === undefined
          ? {
              actions: (
                <>
                  {pendingRewriteSectionCount > 0 && (
                    <ProposalDraftingResumeButton
                      buildActive={proposalBuildActive}
                      busy={resumeAttempt?.pending === true}
                      disabled={sourcesOpen}
                      onResume={resumeProposalDrafting}
                    />
                  )}
                  <Button
                    ref={sourcesButtonRef}
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-label="Sources"
                    disabled={
                      proposalBuildActive ||
                      resumeAttempt?.pending === true ||
                      sections.some((candidate) => refine.isStreaming(candidate.sectionId))
                    }
                    onClick={() => setSourcesOpen(true)}
                  >
                    <Library className="size-4" aria-hidden="true" />
                    Sources
                    {sourceCount !== null && <Badge variant="secondary">{sourceCount}</Badge>}
                  </Button>
                  <CanvasExportControl controller={exportController} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" size="icon-sm" variant="ghost" aria-label="Proposal actions">
                        <MoreHorizontal className="size-4" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem variant="destructive" onSelect={() => setConfirmingDelete(true)}>
                        Delete proposal
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ),
              notice: (
                <>
                  <CanvasExportRefusal controller={exportController} />
                  {resumeAttempt?.failure !== undefined && (
                    <div className="flex items-start gap-2 pt-3">
                      <p role="alert" className="min-w-0 flex-1 pt-1 text-xs break-words text-destructive">
                        {resumeAttempt.failure}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Dismiss drafting error"
                        onClick={() => setResumeAttempt(undefined)}
                      >
                        <X className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </>
              )
            }
          : {})}
      />
      {sourcesOpen && !proposalBuildActive && resumeAttempt?.pending !== true && (
        <ProposalSourcesPanel
          projectId={projectId}
          proposalId={proposalId}
          templateDocId={proposal.templateDocId}
          open={sourcesOpen}
          onOpenChange={setSourcesOpen}
          onChange={setSourceCount}
          trigger={null}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            sourcesButtonRef.current?.focus()
          }}
        />
      )}
      {/* The refusal goes in the header, on a line of its own beneath the action row: the
          row is controls, and a sentence among them reads as a label for whichever one it
          lands beside. What the export reported afterwards stays out here, where wrapping
          text costs the artifact no pinned height. */}
      {bodyEdit === undefined && <CanvasExportNotice controller={exportController} />}
      <ArtifactBuildRunStrip
        key={`proposal:${projectId}:${proposalId}`}
        kind="proposal"
        projectId={projectId}
        artifactId={proposalId}
        services={svc}
        onSettled={reload}
        refreshToken={proposalBuildRefresh}
        onActivityChange={setProposalBuildActive}
        onChooseSources={() => setSourcesOpen(true)}
      />
      <article className="mx-auto w-full max-w-[46rem] px-5 py-8 sm:px-7">
        <div className="space-y-8">
          {sections.map((candidate, position) => {
            const focused = candidate.sectionId === sectionId
            const editingBody = bodyEdit?.sectionId === candidate.sectionId
            // Live refinement feedback for THIS section only: the optimistic instruction turn is
            // there the moment the stream opens, the partial draft streams beneath it, and the
            // committed turn replaces both once the stream closes. Everything clears when the
            // settle-reload refetches persisted history. Sibling sections read all three as
            // undefined and render nothing.
            const pendingUserTurn = refine.optimisticTurn(candidate.sectionId)
            const settledAssistantTurn = refine.committedTurn(candidate.sectionId)
            const liveAssistantText = settledAssistantTurn?.content ?? refine.streamingText(candidate.sectionId)
            return (
              <section
                key={candidate.sectionId}
                ref={focused ? focusedSectionRef : undefined}
                data-testid={`proposal-section-${candidate.sectionId}`}
                {...(focused ? { 'aria-current': 'location' as const } : {})}
                className={cn(
                  // Clears the complete sticky identity header, including the artifact
                  // summary, which the focused section would otherwise arrive underneath.
                  'scroll-mt-80 rounded-lg px-4 py-4 transition-[background-color,box-shadow] duration-300 motion-reduce:transition-none',
                  focused && 'bg-primary/[0.045] ring-1 ring-primary/20',
                  bodyEdit !== undefined && !editingBody && 'opacity-50',
                  editingBody && 'bg-primary/[0.035] ring-2 ring-primary/20'
                )}
              >
                <header className="mb-3 flex flex-wrap items-start gap-x-3 gap-y-2 sm:flex-nowrap">
                  <span
                    data-testid={`proposal-section-${candidate.sectionId}-ordinal`}
                    className="flex h-8 shrink-0 items-center text-xs font-medium text-muted-foreground tabular-nums"
                  >
                    {position + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    {bodyEdit !== undefined ? (
                      <h4 className="text-base leading-6 font-semibold">{candidate.heading}</h4>
                    ) : renamingSectionId === candidate.sectionId ? (
                      <InlineEditableText
                        value={candidate.heading}
                        editing
                        onEditingChange={(next) => {
                          if (!next) setRenamingSectionId(undefined)
                        }}
                        textClassName="text-base leading-6 font-semibold"
                        ariaLabel={`Rename ${candidate.heading}`}
                        onSave={async (next) => {
                          await svc.renameSection(projectId, proposalId, candidate.sectionId, next)
                          reload()
                        }}
                        renderDisplay={(start) => (
                          <button
                            type="button"
                            onClick={start}
                            className="block w-full truncate rounded-xs text-left text-base leading-6 font-semibold hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                          >
                            {candidate.heading}
                          </button>
                        )}
                      />
                    ) : (
                      <ProposalSectionActions
                        section={candidate}
                        isFirst={position === 0}
                        isLast={position === sections.length - 1}
                        heading={(renameControl) => (
                          // The rename control lives inside the heading so the title is
                          // written once; the h4 keeps the section's outline semantics.
                          <h4 className="text-base leading-6 font-semibold">{renameControl}</h4>
                        )}
                        onRename={async (heading) => {
                          await svc.renameSection(projectId, proposalId, candidate.sectionId, heading)
                          reload()
                        }}
                        onDelete={async () => {
                          await svc.deleteSection(projectId, proposalId, candidate.sectionId)
                          reload()
                        }}
                        onMove={(direction) => moveSection(candidate, direction)}
                      />
                    )}
                    <p className="mt-0.5 text-xs text-muted-foreground">{proposalSectionStateLabel(candidate)}</p>
                  </div>
                  {bodyEdit === undefined && (
                    <div className="flex shrink-0 basis-full items-center justify-end gap-1.5 sm:basis-auto">
                      <Button
                        ref={(node) => {
                          if (node === null) bodyEditButtonRefs.current.delete(candidate.sectionId)
                          else bodyEditButtonRefs.current.set(candidate.sectionId, node)
                        }}
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Edit ${candidate.heading} body`}
                        onClick={() => startBodyEditing(candidate)}
                      >
                        <Pencil className="size-3.5" aria-hidden="true" />
                      </Button>
                      {candidate.state !== 'pending' && (
                        <ProposalSectionAcceptance
                          projectId={projectId}
                          proposalId={proposalId}
                          section={candidate}
                          getMessages={services.getSectionMessages}
                          select={services.selectSectionVersion}
                          deselect={services.deselectSectionVersion}
                          onChanged={reload}
                        />
                      )}
                    </div>
                  )}
                </header>
                <div data-testid={`proposal-section-${candidate.sectionId}-body-slot`} className="text-sm leading-6">
                  <CanvasEditSaveAnnouncement
                    label={`${candidate.heading} save status`}
                    saved={
                      !editingBody &&
                      savedBody?.sectionId === candidate.sectionId &&
                      savedBody.acknowledgedArtifactVersion === artifactVersion
                    }
                  />
                  {editingBody ? (
                    <ProposalCanvasBodyEditor
                      section={candidate}
                      saving={bodyEdit.saving}
                      {...(bodyEdit.error === undefined ? {} : { error: bodyEdit.error })}
                      onCancel={() => {
                        setRestoreBodyEditFocus(candidate.sectionId)
                        setBodyEdit(undefined)
                      }}
                      onSave={(draft) => {
                        setBodyEdit((current) =>
                          current?.sectionId === candidate.sectionId
                            ? { sectionId: current.sectionId, mark: proposal, saving: true }
                            : current
                        )
                        void (async () => {
                          try {
                            await svc.editSection(projectId, proposalId, candidate.sectionId, draft)
                            const requestedReloadAttempt = reload()
                            setSavedBody({
                              sectionId: candidate.sectionId,
                              requestedReloadAttempt
                            })
                            setRestoreBodyEditFocus(candidate.sectionId)
                            setBodyEdit(undefined)
                          } catch (editError) {
                            setBodyEdit({
                              sectionId: candidate.sectionId,
                              mark: proposal,
                              saving: false,
                              error: failureLine(editError, loadFailureFallback)
                            })
                          }
                        })()
                      }}
                    />
                  ) : candidate.state === 'pending' && candidate.kind === 'rewrite' ? (
                    <TemplateGuidance markdown={candidate.draftMarkdown} />
                  ) : candidate.draftMarkdown.trim() === '' ? (
                    <p className="text-muted-foreground italic">Not written yet.</p>
                  ) : (
                    <MarkdownRenderer>{candidate.draftMarkdown}</MarkdownRenderer>
                  )}
                  {!editingBody &&
                    savedBody?.sectionId === candidate.sectionId &&
                    savedBody.acknowledgedArtifactVersion === artifactVersion && (
                      <CanvasEditActions
                        state="saved"
                        announceState={false}
                        onSave={() => undefined}
                        onCancel={() => undefined}
                      />
                    )}
                </div>
                {candidate.selectedMessageId !== undefined && (
                  <p className="mt-3 text-xs text-muted-foreground">Selected version {candidate.selectedMessageId}</p>
                )}
                {bodyEdit === undefined && candidate.state !== 'pending' && (
                  <ProposalSectionVersions
                    projectId={projectId}
                    proposalId={proposalId}
                    section={candidate}
                    getMessages={services.getSectionMessages}
                    select={services.selectSectionVersion}
                    deselect={services.deselectSectionVersion}
                    onChanged={reload}
                  />
                )}
                {bodyEdit === undefined && candidate.state === 'pending' && candidate.kind !== 'rewrite' && (
                  <PendingManualSectionAction section={candidate} onWrite={() => startBodyEditing(candidate)} />
                )}
                {bodyEdit === undefined && candidate.state !== 'pending' && (
                  <div className="mt-4 border-t pt-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-muted-foreground">Refine this section</p>
                      {candidate.kind === 'rewrite' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={refine.isStreaming(candidate.sectionId)}
                          onClick={() => refine.startRegenerate(candidate.sectionId)}
                        >
                          Regenerate
                        </Button>
                      )}
                    </div>
                    <div className="mt-2">
                      {(pendingUserTurn !== undefined || liveAssistantText !== undefined) && (
                        <div className="mb-2 space-y-2 rounded-md bg-muted/25 px-3 py-2">
                          {pendingUserTurn !== undefined && (
                            <p className="text-sm">
                              <span className="font-medium">You</span>
                              <span className="mt-0.5 block break-words whitespace-pre-wrap text-muted-foreground">
                                {pendingUserTurn.content}
                              </span>
                            </p>
                          )}
                          {liveAssistantText !== undefined && (
                            <p className="text-sm">
                              <span className="font-medium">Snowvault</span>
                              <span className="mt-0.5 block break-words whitespace-pre-wrap text-muted-foreground">
                                {liveAssistantText}
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                      <RefineComposer
                        placeholder={`Refine ${candidate.heading}`}
                        label={`Refining ${candidate.heading}`}
                        busy={refine.isStreaming(candidate.sectionId)}
                        // The failure belongs to the section whose stream produced it; a sibling's
                        // composer must stay clean.
                        error={refine.errorFor(candidate.sectionId)}
                        onSubmit={(instruction) => refine.startRefine(candidate.sectionId, instruction)}
                        onStop={refine.stop}
                      />
                    </div>
                  </div>
                )}
              </section>
            )
          })}
        </div>
        {bodyEdit === undefined && (
          <div className="pt-6">
            <AddSectionRow
              onAdd={async (heading) => {
                const created = await svc.createSection(projectId, proposalId, heading)
                setRenamingSectionId(created.sectionId)
                reload()
              }}
            />
          </div>
        )}
      </article>
      <CanvasConfirmDialog
        open={confirmingDelete}
        destructive
        title={`Delete “${proposal.documentName}”?`}
        body={`This permanently removes “${proposal.documentName}” and its section history.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          await svc.deleteProposal(projectId, proposalId)
          onClose?.()
        }}
        onOpenChange={setConfirmingDelete}
      />
    </div>
  )
}

function SlideSources({
  slide,
  sourceArtifacts,
  services
}: {
  slide: PresentationSlide
  sourceArtifacts: Array<PresentationSourceArtifact>
  services: ConversationPanelServices
}) {
  const [expanded, setExpanded] = useState(false)
  const documentIDs = [...new Set(slide.sourceDocIds)]
  const artifactIDs = [...new Set(slide.sourceArtifactIds)]
  const artifactByID = new Map(sourceArtifacts.map((artifact) => [artifact.sourceArtifactId, artifact]))
  const selectedArtifacts = artifactIDs.flatMap((artifactID) => {
    const artifact = artifactByID.get(artifactID)
    return artifact === undefined ? [] : [artifact]
  })
  const sourceCount = documentIDs.length + artifactIDs.length
  if (sourceCount === 0) return null
  return (
    <div className="mt-4 border-t pt-3">
      <button
        type="button"
        className="text-xs font-medium text-foreground hover:underline"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        Sources ({sourceCount})
      </button>
      {expanded && (
        <div className="mt-2 space-y-3 rounded-md bg-muted/25 px-3 py-2">
          {documentIDs.length > 0 && <SourceRows docIds={documentIDs} services={services} />}
          {selectedArtifacts.length > 0 && (
            <div role="list" className="divide-y">
              {selectedArtifacts.map((artifact) => (
                <div key={artifact.sourceArtifactId} role="listitem" className="py-2 text-sm">
                  <p className="font-medium break-words">{artifact.artifactTitle}</p>
                  <p className="text-xs text-muted-foreground">Proposal snapshot</p>
                  <p className="text-xs text-muted-foreground">
                    Source updated {formatObservedTime(artifact.sourceUpdatedAtTsMs)} · Captured{' '}
                    {formatObservedTime(artifact.capturedOnTsMs)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {artifact.originAvailable ? 'Origin available' : 'Origin unavailable'}
                  </p>
                </div>
              ))}
            </div>
          )}
          {selectedArtifacts.length < artifactIDs.length && (
            <p className="text-xs text-muted-foreground">Some artifact snapshot details are unavailable.</p>
          )}
        </div>
      )}
    </div>
  )
}

function SlideSpecification({
  projectId,
  presentation,
  slide,
  position,
  focused,
  anchorRef,
  services,
  onVersionsChanged,
  isCover,
  editing,
  editingElsewhere,
  saved,
  saving,
  saveError,
  onStartEdit,
  onCancelEdit,
  onSaveRequest,
  refining,
  refineError,
  onRefine
}: {
  projectId: number
  slide: PresentationSlide
  // Where the slide sits in the deck, counted from one. The stored ordinal starts at
  // zero, and the rail's outline shows the same number as the document.
  position: number
  presentation: Presentation
  focused: boolean
  anchorRef?: RefObject<HTMLElement | null>
  services: ConversationPanelServices
  onVersionsChanged: () => void
  isCover: boolean
  editing: boolean
  editingElsewhere: boolean
  saved: boolean
  saving: boolean
  saveError: string | null
  onStartEdit: () => void
  onCancelEdit: () => void
  onSaveRequest: (request: UpdatePresentationSlideRequest) => Promise<void>
  refining: boolean
  refineError: string | null
  onRefine: (instruction: string) => void
}) {
  const editor = usePresentationSlideDraft({ slide, fitPolicy: presentation.fitPolicy, isCover, active: editing })
  const titleRef = useRef<HTMLInputElement>(null)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const wasEditingRef = useRef(editing)
  const content = editing ? editor.draft : slide
  const fit = editing ? editor.fit : slide.fit
  const policy = presentation.fitPolicy.layouts[content.layout]
  const fitLabel = fit.status.charAt(0).toUpperCase() + fit.status.slice(1)
  const firstFitReason = fit.issues[0] === undefined ? undefined : fitIssueLabel(fit.issues[0])
  const cancelEdit = useCallback(() => {
    editor.reset()
    onCancelEdit()
  }, [editor, onCancelEdit])
  const saveEdit = useCallback(() => {
    if (fit.issues.length > 0) return
    void onSaveRequest(editor.request())
  }, [editor, fit.issues.length, onSaveRequest])
  const onKeyDown = useCanvasEditShortcuts({
    disabled: !editing || saving,
    onSave: saveEdit,
    onCancel: cancelEdit
  })
  useEffect(() => {
    if (editing) titleRef.current?.focus()
  }, [editing, saveError])
  useEffect(() => {
    if (wasEditingRef.current && !editing) editButtonRef.current?.focus()
    wasEditingRef.current = editing
  }, [editing])
  // The reference renders the gauge as a percentage of the layout's capacity, not as raw
  // capacity units; only the bar itself clamps at full.
  const usedPercent = Math.round((fit.usedUnits / fit.capacityUnits) * 100)
  const width = `${Math.min(100, usedPercent)}%`
  return (
    // The section owns editor-wide shortcuts while native fields retain their own semantics.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <section
      ref={anchorRef}
      data-testid={`presentation-slide-${slide.slideId}`}
      {...(editing ? { 'data-canvas-editing': 'true' } : {})}
      {...(focused ? { 'aria-current': 'location' as const } : {})}
      onKeyDown={onKeyDown}
      className={cn(
        // Clears the complete sticky identity header, as with the proposal section above.
        'scroll-mt-80 rounded-xl border bg-background px-5 py-5 transition-[background-color,box-shadow] duration-300 motion-reduce:transition-none',
        focused && 'bg-primary/[0.035] ring-2 ring-primary/20',
        editingElsewhere && 'opacity-50',
        editing && 'bg-primary/[0.035] ring-2 ring-primary/20'
      )}
    >
      <CanvasEditSaveAnnouncement label={`Slide ${position} save status`} saved={saved} />
      <header className="mb-4 flex items-start gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
          {position}
        </span>
        <div className="min-w-0 flex-1">
          <div data-testid={`presentation-slide-${slide.slideId}-layout-slot`} className="min-h-7">
            {editing ? (
              <Select
                value={content.layout}
                disabled={isCover || saving}
                onValueChange={(next) => editor.setLayout(next as PresentationSlide['layout'])}
              >
                <SelectTrigger
                  aria-label="Layout"
                  data-canvas-edit-shortcuts="true"
                  className="h-7 w-fit border-transparent bg-transparent px-1.5 py-0 text-xs text-muted-foreground shadow-none hover:border-border focus-visible:bg-background"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value={PresentationLayout.TitleAndContent}>Title and content</SelectItem>
                  <SelectItem value={PresentationLayout.TwoColumn}>Two column</SelectItem>
                  <SelectItem value={PresentationLayout.SectionHeader}>Section header</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">{content.layout.replaceAll('_', ' ')}</p>
            )}
          </div>
          <h3
            data-testid={`presentation-slide-${slide.slideId}-title-slot`}
            className="mt-1 min-w-0 text-base leading-6 font-semibold"
          >
            {editing ? (
              <InlineArtifactInput
                ref={titleRef}
                aria-label="Slide title"
                value={content.title}
                disabled={saving}
                className="-my-px -ml-1 py-0 text-base leading-6 font-semibold"
                onChange={(event) => editor.setTitle(event.currentTarget.value)}
              />
            ) : (
              content.title
            )}
          </h3>
        </div>
        <div
          className={cn('flex shrink-0 items-center gap-1.5', (editing || editingElsewhere) && 'invisible')}
          {...(editing || editingElsewhere ? { 'aria-hidden': true, inert: true } : {})}
        >
          <Button
            ref={editButtonRef}
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label={`Edit slide ${slide.title}`}
            onClick={onStartEdit}
          >
            <Pencil className="size-3.5" aria-hidden="true" />
          </Button>
          <PresentationSlideAcceptance
            projectId={projectId}
            presentationId={presentation.presentationId}
            slide={slide}
            getMessages={services.getPresentationSlideMessages}
            select={services.selectPresentationSlideVersion}
            deselect={services.deselectPresentationSlideVersion}
            describeIssue={fitIssueLabel}
            onChanged={onVersionsChanged}
          />
        </div>
      </header>
      <dl className="space-y-4 text-sm">
        <div data-testid={`presentation-slide-${slide.slideId}-key-message-slot`}>
          <dt className="text-xs font-medium text-muted-foreground">Key message</dt>
          <dd className="mt-1 font-medium">
            {editing ? (
              <InlineArtifactTextarea
                aria-label="Key message"
                value={content.keyMessage}
                disabled={saving}
                className="-ml-1 font-medium"
                onChange={(event) => editor.setKeyMessage(event.currentTarget.value)}
              />
            ) : (
              content.keyMessage
            )}
          </dd>
        </div>
        <div data-testid={`presentation-slide-${slide.slideId}-points-slot`}>
          <dt className="flex justify-between gap-3 text-xs font-medium text-muted-foreground">
            On-slide points{' '}
            <span>
              {content.contentPoints.length} of {policy.maxPoints}
            </span>
          </dt>
          <dd className="mt-2">
            <ul className="list-disc space-y-1 pl-5">
              {content.contentPoints.map((point, index) => (
                <li key={`${slide.slideId}:${index}`} className="group pl-0.5">
                  {editing ? (
                    <div className="flex min-w-0 items-start gap-1">
                      <InlineArtifactTextarea
                        aria-label={`Point ${index + 1}`}
                        value={point}
                        disabled={saving}
                        className="-ml-1 flex-1"
                        onChange={(event) => editor.setPoint(index, event.currentTarget.value)}
                      />
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Remove point ${index + 1}`}
                        data-canvas-edit-shortcuts="true"
                        disabled={saving}
                        className="opacity-60 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
                        onClick={() => editor.removePoint(index)}
                      >
                        <X className="size-3.5" aria-hidden="true" />
                      </Button>
                    </div>
                  ) : (
                    point
                  )}
                </li>
              ))}
            </ul>
            {editing && !isCover && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-canvas-edit-shortcuts="true"
                disabled={saving}
                className="mt-2 -ml-2 text-muted-foreground"
                onClick={editor.addPoint}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Add point
              </Button>
            )}
          </dd>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div data-testid={`presentation-slide-${slide.slideId}-visual-direction-slot`}>
            <dt className="text-xs font-medium text-muted-foreground">Visual direction</dt>
            <dd className="mt-1">
              {editing ? (
                <InlineArtifactTextarea
                  aria-label="Visual direction"
                  value={content.visualDirection}
                  disabled={saving}
                  className="-ml-1"
                  placeholder="None specified."
                  onChange={(event) => editor.setVisualDirection(event.currentTarget.value)}
                />
              ) : (
                content.visualDirection || 'None specified.'
              )}
            </dd>
          </div>
          <div data-testid={`presentation-slide-${slide.slideId}-speaker-notes-slot`}>
            <dt className="text-xs font-medium text-muted-foreground">Speaker notes</dt>
            <dd className="mt-1 text-muted-foreground">
              {editing ? (
                <InlineArtifactTextarea
                  aria-label="Speaker notes"
                  value={content.speakerNotes}
                  disabled={saving}
                  className="-ml-1 text-muted-foreground"
                  placeholder="None."
                  onChange={(event) => editor.setSpeakerNotes(event.currentTarget.value)}
                />
              ) : (
                content.speakerNotes || 'None.'
              )}
            </dd>
          </div>
        </div>
      </dl>
      {!editing && (
        <>
          <SlideSources slide={slide} sourceArtifacts={presentation.sourceArtifacts} services={services} />
          <PresentationSlideVersions
            projectId={projectId}
            presentationId={presentation.presentationId}
            slide={slide}
            getMessages={services.getPresentationSlideMessages}
            select={services.selectPresentationSlideVersion}
            deselect={services.deselectPresentationSlideVersion}
            describeIssue={fitIssueLabel}
            onChanged={onVersionsChanged}
          />
        </>
      )}
      {!editing && !editingElsewhere && (
        <div className="mt-4 border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground">Refine this slide</p>
          <div className="mt-2">
            <RefineComposer
              placeholder={`Refine ${slide.title}`}
              label={`Refining ${slide.title}`}
              busy={refining}
              error={refineError}
              onSubmit={onRefine}
            />
          </div>
        </div>
      )}
      <div className="mt-5">
        <div
          className="h-1.5 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={`Uses ${usedPercent} percent of the layout's capacity`}
        >
          <div
            className={cn('h-full rounded-full', fit.status === 'overflow' ? 'bg-warning' : 'bg-primary')}
            style={{ width }}
          />
        </div>
        <div className="mt-2 flex justify-between gap-3 text-xs text-muted-foreground">
          <span>{usedPercent}% of capacity</span>
          <span>{fitLabel}</span>
        </div>
        {!editing && fit.issues.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-warning">
            {fit.issues.map((issue) => (
              <li key={issue}>{fitIssueLabel(issue)}</li>
            ))}
          </ul>
        )}
        {!editing && slide.selectedVersionId !== null && (
          <p className="mt-2 text-xs text-muted-foreground">Selected version {slide.selectedVersionId}</p>
        )}
      </div>
      {editing && (
        <CanvasEditActions
          state={saving ? 'saving' : 'editing'}
          fit={{
            label: presentationFitLabel[fit.status],
            ...(firstFitReason === undefined ? {} : { blockingReason: firstFitReason })
          }}
          {...(saveError === null ? {} : { error: saveError })}
          saveDisabled={fit.issues.length > 0}
          onSave={saveEdit}
          onCancel={cancelEdit}
        />
      )}
      {!editing && saved && (
        <CanvasEditActions state="saved" announceState={false} onSave={() => undefined} onCancel={() => undefined} />
      )}
    </section>
  )
}

// A document is one body with a history, so the canvas shows the current revision and
// lists the rest. No outline is published to the rail: a revision list is a history, not
// a structure, and the rail's outline is what makes it the canvas's navigation.
function DocumentPanel({
  projectId,
  documentId,
  scope,
  provenance,
  services,
  onClose
}: {
  projectId: number
  documentId: number
  scope: string
  provenance?: CanvasProvenance
  services: ConversationPanelServices
  // The canvas's own close, the same one PanelHeader's sibling controls use to clear the
  // canvas search params. Delete document is the only in-panel action that needs it.
  onClose?: () => void
}) {
  // One merge for the optional mutation surface, as in the proposal canvas above.
  const svc = mergePanelServices(services)
  const revisionMark = useArtifactRevision(
    projectId,
    (event) => event.kind === 'document' && event.documentId === documentId
  )
  // The revision-chip mark folds into the identity exactly as it folded into the old load
  // key, so a redraft-driven reread starts; the retain key leaves it out, so the reader
  // keeps the page they were reading until that reread lands.
  const identity = `${projectId}:${documentId}:${revisionMark}`
  const retainKey = `${projectId}:${documentId}`
  // The selection carries the revision mark it was made under rather than being reset by
  // an effect: a redraft lands a newer revision, and a reader who was looking at the
  // current one should follow it rather than stay pinned to what is now the previous one.
  const [selection, setSelection] = useState<{ mark: number; versionId: number }>()
  const selectedVersionId = selection?.mark === revisionMark ? selection.versionId : undefined
  const {
    artifact: document,
    failed,
    reload
  } = usePanelArtifact<WorkspaceDocument>({
    identity,
    retainKey,
    enabled: true,
    load: async () => {
      const value = await services.getDocument(projectId, documentId)
      if (value.projectId !== projectId || value.documentId !== documentId) {
        throw new Error('The document identity did not match this workspace.')
      }
      return value
    }
  })
  // Chosen above the returns below, because the two controls in the header act on the
  // revision on screen and hooks cannot be called after a return.
  const revisions = document === null ? [] : [...document.revisions].sort((left, right) => right.ordinal - left.ordinal)
  const current =
    revisions.find((revision) => revision.versionId === selectedVersionId) ??
    revisions.find((revision) => revision.versionId === document?.currentVersionId) ??
    revisions[0]
  // The element rendering the revision on screen, so a copy carries the markup a reader is
  // reading rather than the document's current revision.
  const renderedRef = useRef<HTMLDivElement | null>(null)
  // No reload: a document export writes nothing back, so there is nothing to read again.
  // One shared identity rather than a fresh arrow per render, so the controller's
  // notify-ref effect does not re-run on every render for no gain.
  const exportController = useCanvasExport({
    projectId,
    target: { kind: 'document', documentId, revision: current ?? unloadedRevision },
    services,
    onExported: noExportReload
  })
  const copyController = useCanvasCopy({ revision: current ?? unloadedRevision, renderedRef })
  // Chosen before the returns below, because the rename editor and the delete gate are
  // rendered past them and hooks cannot be called after a return.
  const [titleEditing, setTitleEditing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  if (document === null) {
    if (failed) return <PanelFailure message="This document could not be loaded." retry={reload} />
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading document</p>
  }
  if (current === undefined) return <PanelFailure message="This document has no revisions." />
  return (
    <div data-testid="document-canvas">
      <PanelHeader
        title={document.title}
        icon={<Mail className="size-4 text-primary" aria-hidden="true" />}
        scope={scope}
        {...(provenance === undefined ? {} : { provenance })}
        actions={
          <>
            <CanvasCopyControl controller={copyController} />
            <CanvasExportControl controller={exportController} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="icon-sm" variant="ghost" aria-label="Document actions">
                  <MoreHorizontal className="size-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem variant="destructive" onSelect={() => setConfirmingDelete(true)}>
                  Delete document
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        notice={<CanvasExportRefusal controller={exportController} />}
      />
      {/* The refusal is in the header for the reason the proposal canvas has, and what an
          export reported stays here. Both notices below stay mounted whether or not they
          have anything to say, so both pad their own lines and neither takes any height
          until it does. */}
      <CanvasExportNotice controller={exportController} />
      <CanvasCopyNotice controller={copyController} />
      {/* The editable title sits in the artifact's own header area, exactly as the proposal
          canvas places it: the sticky PanelHeader keeps its static copy for orientation while
          scrolling, and the rename affordance gets the content column's full width here. */}
      <article className="mx-auto w-full max-w-[46rem] px-5 pt-6 sm:px-7">
        <header className="border-b pb-5">
          <InlineEditableText
            value={document.title}
            editing={titleEditing}
            onEditingChange={setTitleEditing}
            textClassName="text-lg leading-7 font-semibold"
            ariaLabel={`Rename ${document.title}`}
            onSave={async (next) => {
              await svc.renameDocument(projectId, documentId, next)
              reload()
            }}
            renderDisplay={(start) => (
              <button
                type="button"
                onClick={start}
                className="block w-full truncate rounded-xs text-left text-lg leading-7 font-semibold hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                {document.title}
              </button>
            )}
          />
        </header>
      </article>
      <div className="border-b px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            data-testid="document-revision"
            className="rounded-md bg-accent-strong px-2 py-0.5 text-xs font-medium text-accent-foreground"
          >
            {documentRevisionLabel(current.ordinal)}
          </span>
          {current.versionId !== document.currentVersionId && (
            <span className="text-xs text-muted-foreground">Not the current revision</span>
          )}
        </div>
        {revisions.length > 1 && (
          <ul className="mt-3 flex flex-wrap gap-1" aria-label="Document revisions">
            {revisions.map((revision) => (
              <li key={revision.versionId}>
                <Button
                  type="button"
                  size="sm"
                  variant={revision.versionId === current.versionId ? 'secondary' : 'ghost'}
                  className="h-auto px-2 py-1 text-xs font-normal"
                  aria-current={revision.versionId === current.versionId}
                  onClick={() => setSelection({ mark: revisionMark, versionId: revision.versionId })}
                >
                  {documentRevisionLabel(revision.ordinal)}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div ref={renderedRef} className="mx-auto w-full max-w-[46rem] px-4 py-5 text-[0.95rem] leading-7 sm:px-5">
        <MarkdownRenderer>{current.content}</MarkdownRenderer>
      </div>
      <CanvasConfirmDialog
        open={confirmingDelete}
        destructive
        title={`Delete “${document.title}”?`}
        body={`This permanently removes “${document.title}”, its revision history, and the chat turn that produced it.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          await svc.deleteDocument(projectId, documentId)
          onClose?.()
        }}
        onOpenChange={setConfirmingDelete}
      />
    </div>
  )
}

// The reference names revisions by letter, which reads as a version of a document rather
// than as a row number.
function documentRevisionLabel(ordinal: number): string {
  if (ordinal < 1) return 'Rev'
  if (ordinal > 26) return `Rev ${ordinal}`
  return `Rev ${String.fromCharCode(64 + ordinal)}`
}

function PresentationPanel({
  projectId,
  presentationId,
  slideId,
  scope,
  provenance,
  services
}: {
  projectId: number
  presentationId: number
  slideId?: number
  scope: string
  provenance?: CanvasProvenance
  services: ConversationPanelServices
}) {
  // One merge for the optional mutation surface, as in the proposal canvas above.
  const svc = mergePanelServices(services)
  const revision = useArtifactRevision(projectId, (event) => {
    if (event.kind !== 'presentation' || event.presentationId !== presentationId) return false
    if (slideId === undefined) return true
    // Deck-level events carry no slide id, so a focused slide still follows them.
    return event.slideId === undefined || event.slideId === slideId
  })
  // Identity and retain key split exactly as the proposal canvas splits them: the mark in
  // the identity rereads the deck when it moves, and the key without it holds the copy.
  const identity = `${projectId}:${presentationId}:${slideId ?? 'deck'}:${revision}`
  const retainKey = `${projectId}:${presentationId}:${slideId ?? 'deck'}`
  const focusedSlideRef = useRef<HTMLElement | null>(null)
  const {
    artifact: presentation,
    artifactAttempt,
    artifactVersion,
    failed,
    reload
  } = usePanelArtifact<Presentation>({
    identity,
    retainKey,
    enabled: true,
    load: async () => {
      const value = await services.getPresentation(projectId, presentationId)
      if (value.projectId !== projectId || value.presentationId !== presentationId) {
        throw new Error('The presentation identity did not match this workspace.')
      }
      return value
    }
  })
  useEffect(() => {
    if (presentation === null || slideId === undefined) return
    scrollArtifactAnchorIntoView(focusedSlideRef.current)
  }, [presentation, slideId])
  const { publish } = useCanvasOutline()
  useEffect(() => {
    if (presentation === null) return undefined
    const slides = [...presentation.slides].sort((left, right) => left.ordinal - right.ordinal)
    publish({
      kind: 'presentation',
      id: presentationId,
      entries: slides.map((slide, position) => ({
        ordinal: position + 1,
        title: slide.title,
        state: slide.state,
        ...(slide.selectedVersionId === null
          ? {}
          : { position: { slide: slide.slideId, version: slide.selectedVersionId } })
      })),
      ...(slides.findIndex((slide) => slide.slideId === slideId) < 0
        ? {}
        : { activeOrdinal: slides.findIndex((slide) => slide.slideId === slideId) + 1 })
    })
    return () => publish(undefined)
  }, [presentation, presentationId, publish, slideId])
  // As with the proposal canvas: the deck is exportable whether or not a chat is open, and
  // a successful export flips it to exported server-side, so the canvas reads it again.
  const exportController = useCanvasExport({
    projectId,
    target: { kind: 'presentation', presentationId, presentation: presentation ?? unloadedPresentation },
    services,
    onExported: reload
  })
  const [titleEditing, setTitleEditing] = useState(false)
  // One editor across the deck at a time, keyed by slide id; the editor renders in place of
  // that slide's specification. saving and saveError ride beside it, and the error is fenced
  // to the deck copy it failed against: a reload that delivers a different artifact retires it.
  const [editingSlideId, setEditingSlideId] = useState<number>()
  const [savedSlide, setSavedSlide] = useState<{
    slideId: number
    requestedReloadAttempt: number
    acknowledgedArtifactVersion?: number
  }>()
  const [slideSave, setSlideSave] = useState<{
    slideId: number
    mark: Presentation
    saving: boolean
    error: string | null
  }>()
  // Per-slide refine is one mutation in flight at a time, with the verbatim failure parked
  // on the slide that earned it until the next attempt from anywhere replaces it.
  const [refiningSlideId, setRefiningSlideId] = useState<number>()
  const [refineFailure, setRefineFailure] = useState<{ slideId: number; message: string }>()
  const templateLabel = useSourceDocumentLabel({
    docId: presentation?.themeDocId,
    absentLabel: 'No template',
    load: services.getSourceSummaries
  })
  if (presentation === null) {
    if (failed) return <PanelFailure message="This presentation could not be loaded." retry={reload} />
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading presentation</p>
  }
  if (slideSave !== undefined && !slideSave.saving && slideSave.mark !== presentation) {
    // Render-reset, as useCanvasCopy does: the refusal belonged to a deck copy the reader
    // can no longer be reading, so it stops being shown without an effect or a blank frame.
    setSlideSave(undefined)
  }
  if (
    savedSlide !== undefined &&
    savedSlide.acknowledgedArtifactVersion === undefined &&
    artifactAttempt === savedSlide.requestedReloadAttempt
  ) {
    setSavedSlide({ ...savedSlide, acknowledgedArtifactVersion: artifactVersion })
  } else if (
    savedSlide?.acknowledgedArtifactVersion !== undefined &&
    savedSlide.acknowledgedArtifactVersion !== artifactVersion
  ) {
    setSavedSlide(undefined)
  }
  const slides = [...presentation.slides].sort(
    (left, right) => left.ordinal - right.ordinal || left.slideId - right.slideId
  )
  const selectedSlide = slideId === undefined ? undefined : slides.find((candidate) => candidate.slideId === slideId)
  if (slideId !== undefined && selectedSlide === undefined)
    return <PanelFailure message="This slide is not part of the selected presentation." />
  // No version guard. An address names a slide, and this panel has never rendered anything
  // but the slide's current content, so there was no staleness to report: the guard only
  // refused addresses, and it refused two whole populations of them. A slide with nothing
  // selected is every deck built through chat, because the producer creates the version and
  // the message linking it and never selects it. A slide with a newer version selected is
  // every slide that has since been refined, which turned each earlier turn's
  // `Show this slide` into a dead end one refine after it was written.
  const saveSlide = async (slideIdToSave: number, request: UpdatePresentationSlideRequest) => {
    setSlideSave({ slideId: slideIdToSave, mark: presentation, saving: true, error: null })
    try {
      await svc.updatePresentationSlide(projectId, presentationId, slideIdToSave, request)
      const requestedReloadAttempt = reload()
      setSavedSlide({ slideId: slideIdToSave, requestedReloadAttempt })
      setEditingSlideId(undefined)
      setSlideSave(undefined)
    } catch (saveError) {
      setSlideSave({
        slideId: slideIdToSave,
        mark: presentation,
        saving: false,
        error: failureLine(saveError, loadFailureFallback)
      })
    }
  }
  const refineSlide = (slideIdToRefine: number, instruction: string) => {
    if (refiningSlideId !== undefined) return
    setRefiningSlideId(slideIdToRefine)
    setRefineFailure(undefined)
    void (async () => {
      try {
        await svc.refinePresentationSlide(projectId, presentationId, slideIdToRefine, instruction)
        setRefiningSlideId(undefined)
        reload()
      } catch (slideRefineError) {
        setRefiningSlideId(undefined)
        setRefineFailure({ slideId: slideIdToRefine, message: failureLine(slideRefineError, loadFailureFallback) })
      }
    })()
  }
  return (
    <div>
      <PanelHeader
        title={presentation.title}
        titleContent={
          editingSlideId === undefined ? (
            <InlineEditableText
              value={presentation.title}
              editing={titleEditing}
              onEditingChange={setTitleEditing}
              textClassName="text-sm font-semibold tracking-tight"
              ariaLabel="Rename presentation"
              onSave={async (next) => {
                await svc.renamePresentation(projectId, presentationId, next)
                reload()
              }}
              renderDisplay={(start) => (
                <h2 className="truncate text-sm font-semibold tracking-tight">
                  <button
                    type="button"
                    onClick={start}
                    className="block max-w-full truncate rounded-xs text-left hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    {presentation.title}
                  </button>
                </h2>
              )}
            />
          ) : (
            <h2 className="truncate text-sm font-semibold tracking-tight">{presentation.title}</h2>
          )
        }
        icon={<PresentationIcon className="size-4 text-primary" aria-hidden="true" />}
        scope={scope}
        {...(provenance === undefined ? {} : { provenance })}
        details={
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Purpose</dt>
            <dd className="truncate" title={presentation.purpose}>
              {presentation.purpose}
            </dd>
            <dt className="text-muted-foreground">Audience</dt>
            <dd className="truncate" title={presentation.audience}>
              {presentation.audience}
            </dd>
            <dt className="text-muted-foreground">Template</dt>
            <dd className="truncate" title={templateLabel}>
              {templateLabel}
            </dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd>{presentation.status.replaceAll('_', ' ')}</dd>
            <dt className="text-muted-foreground">Slides</dt>
            <dd>{slides.length}</dd>
          </dl>
        }
        {...(editingSlideId === undefined
          ? {
              actions: <CanvasExportControl controller={exportController} />,
              notice: <CanvasExportRefusal controller={exportController} />
            }
          : {})}
      />
      {/* The refusal is in the header for the reason the proposal canvas has: naming a
          slide makes it a sentence, and the action row holds controls, so it takes a line
          of the header's own beneath that row. */}
      {editingSlideId === undefined && <CanvasExportNotice controller={exportController} />}
      <ArtifactBuildRunStrip
        key={`presentation:${projectId}:${presentationId}`}
        kind="presentation"
        projectId={projectId}
        artifactId={presentationId}
        services={svc}
        onSettled={reload}
      />
      <div className="border-b px-5 py-4 text-sm">
        <p className="text-xs leading-5 text-muted-foreground">
          Slides are shown as specifications. Layout is applied when the presentation is exported.
        </p>
        {editingSlideId === undefined && (
          <DeckRefineDisclosure
            projectId={projectId}
            presentationId={presentationId}
            services={svc}
            onChanged={reload}
          />
        )}
      </div>
      <div className="mx-auto w-full max-w-[46rem] space-y-4 bg-muted/20 px-4 py-5 sm:px-5">
        {slides.map((slide, position) => {
          const slideIdOfSlide = slide.slideId
          const editingThis = editingSlideId === slideIdOfSlide
          return (
            <SlideSpecification
              key={slideIdOfSlide}
              projectId={projectId}
              slide={slide}
              position={position + 1}
              presentation={presentation}
              focused={slideIdOfSlide === slideId}
              services={services}
              onVersionsChanged={reload}
              {...(slideIdOfSlide === slideId ? { anchorRef: focusedSlideRef } : {})}
              // The deck's first slide is its cover; slides themselves carry no such flag.
              isCover={position === 0}
              editing={editingThis}
              editingElsewhere={editingSlideId !== undefined && !editingThis}
              saved={
                savedSlide?.slideId === slideIdOfSlide && savedSlide.acknowledgedArtifactVersion === artifactVersion
              }
              saving={slideSave?.slideId === slideIdOfSlide && slideSave.saving}
              saveError={slideSave?.slideId === slideIdOfSlide ? (slideSave.error ?? null) : null}
              onStartEdit={() => {
                setTitleEditing(false)
                setSavedSlide(undefined)
                setEditingSlideId(slideIdOfSlide)
              }}
              onCancelEdit={() => setEditingSlideId(undefined)}
              onSaveRequest={(request) => saveSlide(slideIdOfSlide, request)}
              refining={refiningSlideId === slideIdOfSlide}
              refineError={refineFailure?.slideId === slideIdOfSlide ? refineFailure.message : null}
              onRefine={(instruction) => refineSlide(slideIdOfSlide, instruction)}
            />
          )
        })}
      </div>
    </div>
  )
}

const collectionLabels: Record<ConversationCollectionKind, string> = {
  proposals: 'Proposals',
  presentations: 'Presentations',
  content: 'Content',
  connectors: 'Connectors'
}

function CollectionIcon({ collection }: { collection: ConversationCollectionKind }) {
  if (collection === 'proposals') return <FileText className="size-4 text-primary" aria-hidden="true" />
  if (collection === 'presentations') return <PresentationIcon className="size-4 text-primary" aria-hidden="true" />
  if (collection === 'content') return <Database className="size-4 text-primary" aria-hidden="true" />
  return <Cable className="size-4 text-primary" aria-hidden="true" />
}

// Where a row goes when it is opened. Every kind has an address now, so a collection is a
// list of things a reader can act on rather than a list with a few live rows in it. A
// proposal with no drafted section opens whole: the identity leaves sectionId off entirely
// rather than carrying one holding undefined, which the canvas identity would refuse.
function collectionRowIdentity(item: ConversationCollectionItem): WorkspaceCanvasIdentity {
  if (item.kind === 'presentation') return { canvas: 'presentation', presentationId: item.id }
  // A content row is opened at the corpus it was listed from, which the row states rather
  // than the collection inferring it from the workspace it happens to be listed for.
  if (item.kind === 'content') return { canvas: 'file', fileId: item.id, scope: item.scope }
  if (item.kind === 'text') return { canvas: 'text', docId: item.id, scope: item.scope }
  if (item.kind === 'connector') return { canvas: 'connector', connectorId: item.id }
  return {
    canvas: 'proposal',
    proposalId: item.id,
    ...(item.sectionId === undefined ? {} : { sectionId: item.sectionId })
  }
}

function CollectionRow({
  item,
  onNavigate
}: {
  item: ConversationCollectionItem
  onNavigate: (identity: WorkspaceCanvasIdentity) => void
}) {
  return (
    <li className="px-5 py-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">{item.title}</p>
            {/* The one thing about a row that the collection's own header cannot say: this
                one belongs to the organisation, so every workspace in it reads the same row
                and a change here is a change everywhere. */}
            {item.scope === 'global' && (
              <Badge variant="secondary" className="shrink-0">
                Organisation
              </Badge>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.description || 'No description'}</p>
          {/* A stored text has no state of its own, so its row ends at the description
              rather than leaving the blank line an empty status used to draw. */}
          {item.status !== '' && (
            <p className="mt-2 text-[0.6875rem] text-muted-foreground capitalize">{item.status.replaceAll('_', ' ')}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Open ${item.kind} ${item.title}`}
            onClick={() => onNavigate(collectionRowIdentity(item))}
          >
            Open
          </Button>
        </div>
      </div>
    </li>
  )
}

// One page of a collection as the panel holds it: the list it answers for, how far into it
// the reader has read, what came back, and whether anything asked for did not.
type CollectionLoad = {
  authorityKey: string
  page: number
  value?: ConversationCollectionPage
  error?: true
}

// The Content collection lists two kinds of row from one page, and a reader scanning it
// needs to know which is which: an uploaded file has a name and a processing state, a
// stored text has neither. Only a group holding something is drawn.
function contentGroups(
  items: Array<ConversationCollectionItem>
): Array<{ key: string; label: string; items: Array<ConversationCollectionContentItem> }> {
  const rowsOfKind = (kind: ConversationCollectionContentItem['kind']) =>
    items.filter((item): item is ConversationCollectionContentItem => item.kind === kind)
  return [
    { key: 'files', label: 'Files', items: rowsOfKind('content') },
    { key: 'text', label: 'Text', items: rowsOfKind('text') }
  ].filter((group) => group.items.length > 0)
}

function CollectionPanel({
  projectId,
  collection,
  scope,
  globalContentEnabled,
  services,
  onNavigate,
  onOpenContentPage
}: {
  projectId: number
  collection: ConversationCollectionKind
  scope: string
  globalContentEnabled: boolean
  services: ConversationPanelServices
  onNavigate: (identity: WorkspaceCanvasIdentity) => void
  onOpenContentPage?: (target: ContentPanelTarget) => void
}) {
  const [search, setSearch] = useState('')
  const [committedSearch, setCommittedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [attempt, setAttempt] = useState(0)
  const manageable = collection === 'content'
  // The organisation's switch is handed down from the host rather than read off the router
  // context here. The workspace route already holds the profile it comes from, and a canvas
  // that reaches into the router for one of its own facts is a canvas its tests have to
  // mount a router for.
  const organisationContent = manageable && globalContentEnabled
  const authorityKey = `${projectId}:${collection}:${organisationContent}:${committedSearch}`
  const loadKey = `${authorityKey}:${page}:${attempt}`
  const [load, setLoad] = useState<CollectionLoad>()
  useEffect(() => {
    let active = true
    const loader = services.loadCollection ?? loadConversationCollection
    void loader(projectId, collection, {
      page,
      search: committedSearch,
      ...(organisationContent ? { globalContent: true } : {})
    })
      .then((value) => {
        if (!active) return
        setLoad((previous) => {
          if (page === 1) return { authorityKey, page, value }
          if (previous?.authorityKey !== authorityKey || previous.page !== page - 1 || previous.value === undefined) {
            return { authorityKey, page, error: true }
          }
          const previousKeys = new Set(previous.value.items.map((item) => `${item.kind}:${item.id}`))
          return {
            authorityKey,
            page,
            value: {
              items: [
                ...previous.value.items,
                ...value.items.filter((item) => !previousKeys.has(`${item.kind}:${item.id}`))
              ],
              hasMore: value.hasMore,
              ...(value.partial === undefined ? {} : { partial: value.partial })
            }
          }
        })
      })
      .catch(() => {
        if (active) {
          setLoad((previous) =>
            previous?.authorityKey === authorityKey && previous.value !== undefined
              ? { ...previous, error: true }
              : { authorityKey, page, error: true }
          )
        }
      })
    return () => {
      active = false
    }
  }, [
    authorityKey,
    collection,
    committedSearch,
    loadKey,
    organisationContent,
    page,
    projectId,
    services.loadCollection
  ])
  const current = load?.authorityKey === authorityKey ? load : undefined
  useEffect(() => {
    if (!manageable || current?.value === undefined || (current.page !== page && !current.error)) return
    const pending = current.value.items.filter(
      (item): item is ConversationCollectionContentItem =>
        item.kind === 'content' &&
        (item.status === RagFileUploadState.Uploading || item.status === RagFileUploadState.Processing)
    )
    if (pending.length === 0) return
    let active = true
    let timer: ReturnType<typeof setTimeout>
    const refresh = async () => {
      const results = await Promise.allSettled(
        pending.map((item) => (services.loadFile ?? loadContentFile)(projectId, item.id, item.scope))
      )
      if (!active) return
      setLoad((previous) => {
        // A newer page, search, write or dialog owns its own list. Polling only
        // updates rows from this read and never resets the accumulated pages.
        if (previous !== current || previous.value === undefined) return previous
        const items = previous.value.items.map((item) => {
          const index = pending.findIndex((candidate) => candidate === item)
          const result = results[index]
          if (result?.status !== 'fulfilled') return item
          const file = result.value
          if (
            file.fileId !== item.id ||
            file.updatedAtTsMs < item.updatedAtTsMs ||
            file.fileUploadState === item.status
          )
            return item
          return { ...item, status: file.fileUploadState, updatedAtTsMs: file.updatedAtTsMs }
        })
        return items.every((item, index) => item === previous.value?.items[index])
          ? previous
          : { ...previous, value: { ...previous.value, items } }
      })
      timer = setTimeout(() => {
        void refresh()
      }, 1500)
    }
    timer = setTimeout(() => {
      void refresh()
    }, 1500)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [current, loadKey, manageable, page, projectId, services.loadFile])
  // Back to the first page, not another go at the one on screen. The page merge only
  // accepts a page one past the one it holds, so re-asking for the current page beyond the
  // first lands on the guard and fails again; and a collection that came back short has
  // nothing accumulated worth keeping anyway. A row the reader has just added or deleted
  // comes back the same way: the search they committed still governs the list, and a
  // window the panel accumulated one page at a time is not the window it was once every
  // row in it has shifted.
  const reloadFromStart = () => {
    setPage(1)
    setAttempt((value) => value + 1)
  }
  const label = collectionLabels[collection].toLowerCase()
  // Rows that were asked for and did not arrive, whether they belong to the page on screen
  // or to the page after it. One line for both, and it names the collection rather than
  // promising the missing rows are further down a list they may well not be on.
  const shortfall = `Some ${label} could not be loaded.`
  if (current?.error && current.value === undefined) {
    return <PanelFailure message={`${collectionLabels[collection]} could not be loaded.`} retry={reloadFromStart} />
  }
  return (
    <div>
      <PanelHeader
        title={collectionLabels[collection]}
        icon={<CollectionIcon collection={collection} />}
        scope={scope}
        {...(manageable && onOpenContentPage !== undefined
          ? {
              actions: (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenContentPage({ page: 'content' })}
                >
                  Manage content in settings
                </Button>
              )
            }
          : {})}
        {...(collection === 'connectors' && onOpenContentPage !== undefined
          ? {
              actions: (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenContentPage({ page: 'connector' })}
                >
                  Manage connectors in settings
                </Button>
              )
            }
          : {})}
      />
      <form
        className="flex gap-2 border-b px-5 py-3"
        onSubmit={(event) => {
          event.preventDefault()
          const next = search.trim()
          setCommittedSearch(next)
          setPage(1)
          setAttempt((value) => value + 1)
        }}
      >
        <Input
          aria-label={`Search ${collectionLabels[collection].toLowerCase()}`}
          value={search}
          maxLength={200}
          placeholder={`Search ${collectionLabels[collection].toLowerCase()}`}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>
      {current?.value === undefined ? (
        <p className="px-5 py-8 text-sm text-muted-foreground">Loading {label}</p>
      ) : current.value.items.length === 0 ? (
        // Nothing on the page is not nothing in the workspace when a source is missing from
        // it: the other source can answer with an empty page perfectly legitimately, and
        // every row there should have been belongs to the one that failed. Saying the
        // workspace is empty would be a lie the rail's count beside it contradicts.
        current.value.partial === true ? (
          <PanelFailure message={shortfall} retry={reloadFromStart} />
        ) : (
          // An empty list is only an empty workspace while the workspace is all that was
          // looked in. With organisation content on, the Content collection spans the
          // organisation's corpus too, and the line has to say so or it sends the reader
          // looking for rows the workspace never held.
          <p className="px-5 py-8 text-sm text-muted-foreground">
            {organisationContent
              ? `No ${label} in this workspace or the organisation.`
              : `No ${label} in this workspace.`}
          </p>
        )
      ) : (
        <>
          {collection === 'content' ? (
            contentGroups(current.value.items).map((group) => (
              <div key={group.key}>
                <h3
                  id={`collection-group-${group.key}`}
                  className="border-b bg-muted/25 px-5 py-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                >
                  {group.label}
                </h3>
                <ul className="divide-y" aria-labelledby={`collection-group-${group.key}`}>
                  {group.items.map((item) => (
                    <CollectionRow key={`${item.kind}:${item.id}`} item={item} onNavigate={onNavigate} />
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <ul className="divide-y">
              {current.value.items.map((item) => (
                <CollectionRow key={`${item.kind}:${item.id}`} item={item} onNavigate={onNavigate} />
              ))}
            </ul>
          )}
          <div className="border-t px-5 py-4">
            {/* Two ways to come back short of what was asked for, one line for both: the
                next page failed to load, or this one arrived missing a source. */}
            {(current.error === true || current.value.partial === true) && (
              <p className="mb-2 text-xs text-destructive">{shortfall}</p>
            )}
            {current.value.hasMore && (
              <Button
                type="button"
                variant="outline"
                disabled={current.page !== page}
                onClick={() => setPage((value) => value + 1)}
              >
                {current.page === page ? 'Load more' : 'Loading more'}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// The content panels take three loaders rather than the whole panel service object: they
// are the only surface that reads the corpus, and nothing else in that object has any
// bearing on a file, a text or a connector. The defaults stand in for a host that binds
// none of them, exactly as the artifact mutations' defaults do.
function contentPanelServices(services: ConversationPanelServices): ContentPanelServices {
  return {
    loadFile: services.loadFile ?? loadContentFile,
    loadText: services.loadText ?? loadContentText,
    loadConnector: services.loadConnector ?? loadContentConnector
  }
}

function CanvasBody({
  projectId,
  projectName,
  canvas,
  provenance,
  globalContentEnabled,
  services,
  onNavigate,
  onOpenContentPage,
  onClose
}: {
  projectId: number
  projectName: string
  canvas: WorkspaceCanvasIdentity
  provenance?: CanvasProvenance
  globalContentEnabled: boolean
  services: ConversationPanelServices
  onNavigate: (identity: WorkspaceCanvasIdentity) => void
  onOpenContentPage?: (target: ContentPanelTarget) => void
  onClose?: () => void
}) {
  const scope = workspaceScope(projectName)
  // A content row's line follows the address rather than the workspace the canvas is open
  // in, because that is the one case where the two differ.
  const contentScopeLine = (contentScope: 'workspace' | 'global') =>
    contentScope === 'global' ? organisationScope : scope
  switch (canvas.canvas) {
    case 'proposal':
      return (
        <ProposalPanel
          projectId={projectId}
          proposalId={canvas.proposalId}
          {...(canvas.sectionId === undefined ? {} : { sectionId: canvas.sectionId })}
          scope={scope}
          {...(provenance === undefined ? {} : { provenance })}
          services={services}
          {...(onClose === undefined ? {} : { onClose })}
        />
      )
    case 'presentation':
      return (
        <PresentationPanel
          projectId={projectId}
          presentationId={canvas.presentationId}
          {...(canvas.slideId === undefined ? {} : { slideId: canvas.slideId })}
          scope={scope}
          {...(provenance === undefined ? {} : { provenance })}
          services={services}
        />
      )
    case 'document':
      return (
        <DocumentPanel
          projectId={projectId}
          documentId={canvas.documentId}
          scope={scope}
          {...(provenance === undefined ? {} : { provenance })}
          services={services}
          {...(onClose === undefined ? {} : { onClose })}
        />
      )
    case 'file':
      return (
        <FilePanel
          projectId={projectId}
          fileId={canvas.fileId}
          contentScope={canvas.scope}
          scope={contentScopeLine(canvas.scope)}
          services={contentPanelServices(services)}
          {...(onOpenContentPage === undefined ? {} : { onOpenContentPage })}
        />
      )
    case 'text':
      return (
        <TextPanel
          projectId={projectId}
          docId={canvas.docId}
          contentScope={canvas.scope}
          scope={contentScopeLine(canvas.scope)}
          services={contentPanelServices(services)}
          {...(onOpenContentPage === undefined ? {} : { onOpenContentPage })}
        />
      )
    case 'connector':
      return (
        <ConnectorPanel
          projectId={projectId}
          connectorId={canvas.connectorId}
          scope={scope}
          services={contentPanelServices(services)}
          {...(onOpenContentPage === undefined ? {} : { onOpenContentPage })}
        />
      )
    case 'collection':
      return (
        <CollectionPanel
          projectId={projectId}
          collection={canvas.collection}
          scope={scope}
          globalContentEnabled={globalContentEnabled}
          services={services}
          onNavigate={onNavigate}
          {...(onOpenContentPage === undefined ? {} : { onOpenContentPage })}
        />
      )
  }
}

function InspectorBody({
  projectId,
  chatTitle,
  inspector,
  state,
  services,
  onNavigate,
  onAnswerSlot
}: {
  projectId: number
  chatTitle?: string
  inspector: ChatInspectorIdentity
  state: ConversationState
  services: ConversationPanelServices
  onNavigate: (identity: ChatInspectorIdentity) => void
  onAnswerSlot?: (stem: string) => void
}) {
  const scope = chatScope(chatTitle)
  switch (inspector.inspector) {
    case 'facts':
      return (
        <FactsPanel
          projectId={projectId}
          briefId={inspector.briefId}
          scope={scope}
          state={state}
          services={services}
          onNavigate={onNavigate}
          {...(onAnswerSlot === undefined ? {} : { onAnswerSlot })}
        />
      )
    case 'sources':
      return (
        <SourcesPanel
          projectId={projectId}
          messageId={inspector.messageId}
          scope={scope}
          state={state}
          services={services}
        />
      )
    case 'activity':
      return <ActivityPanel originMessageId={inspector.originMessageId} scope={scope} state={state} />
  }
}

function canvasKey(canvas: WorkspaceCanvasIdentity): string {
  switch (canvas.canvas) {
    case 'proposal':
      return `proposal:${canvas.proposalId}:${canvas.sectionId}`
    case 'presentation':
      return `presentation:${canvas.presentationId}:${canvas.slideId ?? 'all'}`
    case 'document':
      return `document:${canvas.documentId}`
    case 'file':
      return `file:${canvas.fileId}`
    case 'text':
      return `text:${canvas.docId}`
    case 'connector':
      return `connector:${canvas.connectorId}`
    case 'collection':
      return `collection:${canvas.collection}`
  }
}

function inspectorKey(inspector: ChatInspectorIdentity): string {
  switch (inspector.inspector) {
    case 'facts':
      return `facts:${inspector.briefId}`
    case 'sources':
      return `sources:${inspector.messageId}`
    case 'activity':
      return `activity:${inspector.originMessageId}`
  }
}

function AnimatedSurface({
  surfaceKey,
  testId,
  children
}: {
  surfaceKey: string
  testId: string
  children: ReactNode
}) {
  return (
    <div
      key={surfaceKey}
      data-testid={testId}
      data-surface-key={surfaceKey}
      className="min-h-full animate-in duration-200 ease-out fade-in slide-in-from-right-2 motion-reduce:animate-none motion-reduce:duration-0"
    >
      {children}
    </div>
  )
}

// The workspace's canvas. It is a column beside the chat rather than a panel inside it,
// it can be open with no chat at all, and its width is the reader's to set. ADR-167.
function reserveSheetEscapeForCanvasEditor(event: KeyboardEvent): void {
  const descriptor = Object.getOwnPropertyDescriptor(Event.prototype, 'defaultPrevented')
  if (descriptor?.get === undefined) {
    event.preventDefault()
    return
  }

  let sheetReadPending = true
  try {
    Object.defineProperty(event, 'defaultPrevented', {
      configurable: true,
      get: () => {
        if (sheetReadPending) {
          sheetReadPending = false
          return true
        }
        return descriptor.get?.call(event) ?? false
      }
    })
  } catch {
    event.preventDefault()
    return
  }

  // Radix checks defaultPrevented immediately after onEscapeKeyDown and otherwise prevents the
  // native event before target handlers run. Reserve only that read, then leave the event's real
  // cancellation state to CodeMirror, Crepe and the canvas shortcut bubble.
  queueMicrotask(() => {
    Reflect.deleteProperty(event, 'defaultPrevented')
  })
}

export function WorkspaceCanvas({
  projectId,
  projectName,
  canvas,
  provenance,
  globalContentEnabled = false,
  onClose,
  onNavigate = () => undefined,
  onOpenContentPage,
  onToggleWide,
  wide = false,
  restoreFocus,
  services = defaultServices
}: {
  projectId: number
  projectName: string
  canvas?: WorkspaceCanvasIdentity
  // Where the open document started, which the rail's shell data already knows. The
  // canvas is handed it rather than reading the shell itself, so it stays a component
  // the tests can render on its own. The caller also decides whether it is worth saying:
  // see CanvasProvenance.
  provenance?: CanvasProvenance
  onClose: () => void
  onNavigate?: (identity: WorkspaceCanvasIdentity) => void
  // Whether the organisation's own corpus is switched on, which decides what the Content
  // collection lists. It is the host's fact:
  // the profile it comes from is already in the route's context, and the canvas is passed
  // through three more components before the collection needs it. Off by default, which is
  // the one corpus every workspace had before the switch existed.
  globalContentEnabled?: boolean
  // The way out of a content panel is a handover to scoped settings. The target carries
  // the row's scope so organisation content opens organisation settings and workspace
  // content opens this workspace's settings.
  onOpenContentPage?: (target: ContentPanelTarget) => void
  onToggleWide?: () => void
  wide?: boolean
  restoreFocus?: () => void
  services?: ConversationPanelServices
}) {
  const narrow = useNarrowPanel()
  const { panelRef, width, effectiveMaximum, desktopSpace, resizeBy, commit } = useDesktopPanelWidth()
  const useSheet = narrow || !desktopSpace
  const [resizing, setResizing] = useState(false)
  const close = () => {
    onClose()
    if (!useSheet) queueMicrotask(() => restoreFocus?.())
  }
  const content =
    canvas === undefined ? null : (
      <AnimatedSurface surfaceKey={canvasKey(canvas)} testId="workspace-canvas-content">
        <CanvasBody
          projectId={projectId}
          projectName={projectName}
          canvas={canvas}
          {...(provenance === undefined ? {} : { provenance })}
          globalContentEnabled={globalContentEnabled}
          services={services}
          onNavigate={onNavigate}
          {...(onOpenContentPage === undefined ? {} : { onOpenContentPage })}
          onClose={close}
        />
      </AnimatedSurface>
    )

  if (useSheet) {
    if (canvas === undefined) return <aside ref={panelRef} {...canvasDataAttributes()} aria-label="Document" hidden />
    return (
      <>
        <span ref={panelRef} className="hidden" aria-hidden="true" />
        <Sheet open onOpenChange={(open) => !open && close()}>
          <SheetContent
            {...canvasDataAttributes(canvas)}
            side="right"
            closeLabel="Close document"
            className="w-[min(96vw,46rem)] gap-0 overflow-hidden p-0 motion-reduce:duration-0 sm:max-w-[46rem]"
            onEscapeKeyDown={(event) => {
              const target = event.target
              if (!(target instanceof Element)) return
              const editor = target.closest('[data-canvas-editing="true"]')
              if (editor === null) return
              reserveSheetEscapeForCanvasEditor(event)
            }}
            onCloseAutoFocus={(event) => {
              if (restoreFocus === undefined) return
              event.preventDefault()
              restoreFocus()
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Document</SheetTitle>
              <SheetDescription>Review the open proposal, presentation or collection.</SheetDescription>
            </SheetHeader>
            <div className="shrink-0 border-b px-3 py-2 md:hidden">
              <Button type="button" variant="ghost" size="sm" onClick={close}>
                <ChevronLeft aria-hidden="true" /> Back to chat
              </Button>
            </div>
            <div
              data-testid="workspace-canvas-scroller"
              className="min-h-0 flex-1 [scrollbar-gutter:stable] overflow-y-auto overscroll-contain"
            >
              {content}
            </div>
          </SheetContent>
        </Sheet>
      </>
    )
  }

  const open = canvas !== undefined
  return (
    <aside
      ref={panelRef}
      {...canvasDataAttributes(canvas)}
      aria-label="Document"
      aria-hidden={!open}
      data-state={open ? 'open' : 'closed'}
      data-resizing={resizing ? 'true' : 'false'}
      data-wide={wide ? 'true' : 'false'}
      className={cn(
        // Width is deliberately not transitioned. Animating it animates the chat
        // column's layout, which slides the composer out from under a pointer that
        // was already aimed at Send: mousedown and mouseup then land on different
        // elements and the browser fires no click at all, losing the message with no
        // error. The surface's own entry motion is kept; motion never gates input.
        'relative flex min-h-0 shrink-0 flex-col overflow-hidden bg-background transition-[transform,opacity,border-color] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:duration-0',
        open
          ? 'translate-x-0 border-l opacity-100 duration-300'
          : 'pointer-events-none translate-x-2 border-l border-transparent opacity-0 duration-150',
        wide && 'flex-1',
        resizing && 'transition-none'
      )}
      // Widened, the canvas takes the row through flex-1 and must not also pin a width.
      // Closed, it collapses to nothing rather than to its content.
      style={{ width: wide ? undefined : open ? `${width}px` : '0px' }}
    >
      {open && (
        <>
          {!wide && (
            <PaneResizer
              value={width}
              min={minPanelWidth}
              max={effectiveMaximum}
              onDelta={resizeBy}
              onCommit={commit}
              onResizeStart={() => setResizing(true)}
              onResizeEnd={() => setResizing(false)}
              ariaLabel="Resize document canvas"
              className="absolute inset-y-0 left-0 z-20 -translate-x-1/2"
            />
          )}
          <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
            {onToggleWide !== undefined && (
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label={wide ? 'Show the chat beside the document' : 'Widen the document'}
                aria-pressed={wide}
                onClick={onToggleWide}
              >
                {wide ? (
                  <PanelRight className="size-4" aria-hidden="true" />
                ) : (
                  <Maximize2 className="size-4" aria-hidden="true" />
                )}
              </Button>
            )}
            <Button type="button" size="icon-sm" variant="ghost" aria-label="Close document" onClick={close}>
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>
          <div
            data-testid="workspace-canvas-scroller"
            className="min-h-0 flex-1 [scrollbar-gutter:stable] overflow-y-auto overscroll-contain"
          >
            {content}
          </div>
        </>
      )}
    </aside>
  )
}

// The open chat's inspector. Facts and sources are meaningless without the chat they
// belong to, so this surface only exists while one is open. It docks beside the
// transcript on wide viewports, whatever else the workspace has open, so a citation
// stays readable against the document it is a citation for; narrow viewports get the
// modal sheet, where there is no width to share.
export function ChatInspector({
  projectId,
  chatTitle,
  inspector,
  state,
  onClose,
  onNavigate = () => undefined,
  restoreFocus,
  onAnswerSlot,
  services = defaultServices
}: {
  projectId: number
  chatTitle?: string
  inspector?: ChatInspectorIdentity
  state: ConversationState
  onClose: () => void
  onNavigate?: (identity: ChatInspectorIdentity) => void
  restoreFocus?: () => void
  onAnswerSlot?: (stem: string) => void
  services?: ConversationPanelServices
}) {
  const useSheet = useNarrowPanel()
  const close = () => {
    onClose()
    if (!useSheet) queueMicrotask(() => restoreFocus?.())
  }
  // Seeding the composer while the panel is a modal sheet would hand focus to a
  // textarea the sheet's focus trap immediately takes back, so the press would look
  // like it did nothing. Close first, then seed. Beside the transcript the panel and
  // the composer are both visible, so the panel stays open.
  const answerSlot =
    onAnswerSlot === undefined
      ? undefined
      : (stem: string) => {
          if (useSheet) onClose()
          onAnswerSlot(stem)
        }
  const content =
    inspector === undefined ? null : (
      <AnimatedSurface surfaceKey={inspectorKey(inspector)} testId="chat-inspector-content">
        {state.projectId === projectId ? (
          <InspectorBody
            projectId={projectId}
            {...(chatTitle === undefined ? {} : { chatTitle })}
            inspector={inspector}
            state={state}
            services={services}
            onNavigate={onNavigate}
            {...(answerSlot === undefined ? {} : { onAnswerSlot: answerSlot })}
          />
        ) : (
          <PanelFailure message="Conversation details are unavailable for this workspace." />
        )}
      </AnimatedSurface>
    )

  if (useSheet) {
    if (inspector === undefined)
      return <aside {...inspectorDataAttributes()} aria-label="Sources and activity" hidden />
    return (
      <Sheet open onOpenChange={(open) => !open && close()}>
        <SheetContent
          {...inspectorDataAttributes(inspector)}
          side="right"
          closeLabel="Close sources and activity"
          className="w-[min(92vw,21.25rem)] gap-0 overflow-hidden p-0 motion-reduce:duration-0 sm:max-w-[21.25rem]"
          onCloseAutoFocus={(event) => {
            if (restoreFocus === undefined) return
            event.preventDefault()
            restoreFocus()
          }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sources and activity</SheetTitle>
            <SheetDescription>Review the current facts or sources for this chat.</SheetDescription>
          </SheetHeader>
          <div
            data-testid="chat-inspector-scroller"
            className="min-h-0 flex-1 [scrollbar-gutter:stable] overflow-y-auto overscroll-contain"
          >
            {content}
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  const open = inspector !== undefined
  return (
    <aside
      {...inspectorDataAttributes(inspector)}
      aria-label="Sources and activity"
      aria-hidden={!open}
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'relative flex min-h-0 shrink-0 flex-col overflow-hidden bg-background transition-[transform,opacity,border-color] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:duration-0',
        open
          ? 'translate-x-0 border-l opacity-100 duration-300'
          : 'pointer-events-none translate-x-2 border-l border-transparent opacity-0 duration-150'
      )}
      style={{ width: open ? `${chatInspectorWidth}px` : '0px' }}
    >
      {open && (
        <>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Close sources and activity"
            className="absolute top-2 right-2 z-10"
            onClick={close}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
          <div
            data-testid="chat-inspector-scroller"
            className="min-h-0 flex-1 [scrollbar-gutter:stable] overflow-y-auto overscroll-contain"
          >
            {content}
          </div>
        </>
      )}
    </aside>
  )
}
